// Onboarding tour copy. Kept out of `src/constants/translations.ts` on purpose:
// that file is already ~2.4k lines, and these ~190 keys per language would make
// it materially harder to work in. Same flat-key convention, same LanguageCode,
// same fallback-to-Indonesian rule, and the same parity test
// (src/constants/translations.parity.test.ts): id, zh and en carry every key.
//
// Keys are `tours.<tourId>.<stepKey>.title` / `.body`, `controls.*`, `pill.*`.
// Bodies may contain simple inline HTML (<b>, <br />) — driver.js renders the
// description as HTML.
//
// The copy teaches the CUSTOMS CONCEPT, not the widget. "Click this dropdown"
// is worthless to a first-time PPJK staffer; what the field means to Bea Cukai,
// and what breaks downstream if it is wrong, is the thing worth saying.

import { DEFAULT_LANGUAGE, type LanguageCode } from '@constants/translations';

type CopyDict = Record<string, string>;

const id: CopyDict = {
  // ---- Controls ---------------------------------------------------------
  'controls.next': 'Lanjut',
  'controls.back': 'Kembali',
  'controls.done': 'Selesai',
  'controls.progress': 'Langkah {{current}} dari {{total}}',
  'controls.clickToContinue': 'Klik elemen yang disorot untuk melanjutkan.',
  'controls.interactHint': 'Silakan isi langsung — panduan lanjut sendiri setelah Anda selesai.',
  'controls.exploreHint': 'Menu ini tetap bisa diklik — silakan buka-buka dulu, panduan menunggu di sini.',

  // ---- Help pill --------------------------------------------------------
  'pill.label': 'Panduan',
  'pill.aria': 'Buka panduan halaman ini',
  'pill.pageGuide': 'Panduan halaman ini',
  'pill.tabGuide': 'Panduan tab {{tab}}',
  'pill.menuGuide': 'Panduan menu Jarvis',
  'pill.section.split': 'Panduan pisah dokumen saja',
  'pill.onboarding': 'Pengenalan awal',

  // ---- dashboard-welcome ------------------------------------------------
  'tours.dashboard-welcome.intro.title': 'Selamat datang di Jarvis',
  'tours.dashboard-welcome.intro.body':
    'Jarvis membantu Anda menyiapkan dan mengirim dokumen kepabeanan ke CEISA — sistem Bea Cukai. Panduan singkat ini menunjukkan dari mana harus mulai. Anda bisa keluar kapan saja dengan tombol ✕ atau Esc.',
  'tours.dashboard-welcome.stats.title': 'Ringkasan aktivitas',
  'tours.dashboard-welcome.stats.body':
    'Di sini terlihat berapa dokumen yang Anda buat dan statusnya. Saat pertama kali masuk, bagian ini masih kosong — itu normal.',
  'tours.dashboard-welcome.nav.title': 'Menu di sisi kiri',
  'tours.dashboard-welcome.nav.body':
    'Semua pekerjaan dimulai dari sini. Menu dikelompokkan per jenis pekerjaan, bukan per dokumen — jadi kegiatan impor dan ekspor berada dalam satu kelompok yang sama.',
  'tours.dashboard-welcome.search.title': 'Cari menu',
  'tours.dashboard-welcome.search.body':
    'Kalau lupa letak sebuah menu, ketik saja namanya di sini. Lebih cepat daripada membuka kelompok satu per satu.',
  'tours.dashboard-welcome.kepabeanan.title': 'Mulai dari Kepabeanan',
  'tours.dashboard-welcome.kepabeanan.body':
    'Inilah kelompok yang paling sering Anda pakai. Di dalamnya ada <b>Buat Aju</b> (membuat dokumen baru), <b>Draft</b> (dokumen yang belum dikirim), dan <b>Doc Pabean</b> (dokumen yang sudah dikirim).<br /><br />Silakan klik untuk membukanya.',
  'tours.dashboard-welcome.help.title': 'Panduan selalu ada di sini',
  'tours.dashboard-welcome.help.body':
    'Sebelum lanjut: klik akun Anda di bagian bawah menu untuk membuka menu akun. Di sana ada <b>Panduan</b> — klik kapan saja untuk mengulang panduan halaman yang sedang Anda buka, atau membuka penjelasan lengkap seluruh menu Jarvis.<br /><br />Jadi tidak perlu menghafal apa pun — Anda selalu bisa kembali ke sini.',
  'tours.dashboard-welcome.buatAju.title': 'Mulai dari sini: Buat Aju',
  'tours.dashboard-welcome.buatAju.body':
    'Inilah pintu masuk untuk membuat dokumen baru. Kalau Anda ingin melanjutkan draft yang sudah ada, pakai <b>Draft</b> tepat di bawahnya.<br /><br />Klik <b>Buat Aju</b> untuk membuat PIB pertama Anda — panduan berikutnya akan muncul di sana.',

  // ---- sidebar-map ------------------------------------------------------
  'tours.sidebar-map.intro.title': 'Peta menu Jarvis',
  'tours.sidebar-map.intro.body':
    'Panduan ini menjelaskan setiap kelompok menu secara singkat, supaya Anda tahu ke mana harus pergi. Tidak perlu dihafal — panduan ini bisa dibuka kapan saja.',
  'tours.sidebar-map.mgmtDashboard.title': 'Mgmt Dashboard',
  'tours.sidebar-map.mgmtDashboard.body':
    'Laporan tingkat manajemen: volume dokumen, biaya, dan tren per periode. Biasanya dipakai atasan, bukan petugas harian.',
  'tours.sidebar-map.manifest.title': 'Manifest',
  'tours.sidebar-map.manifest.body':
    'Untuk dokumen manifes kapal (BC 1.1) — daftar seluruh muatan yang dibawa satu sarana pengangkut. Berbeda dengan PIB, yang hanya berisi barang milik satu importir.',
  'tours.sidebar-map.kepabeanan.title': 'Kepabeanan',
  'tours.sidebar-map.kepabeanan.body':
    'Kelompok utama. <b>Buat Aju</b> membuat dokumen baru, <b>Draft</b> menyimpan yang belum dikirim, <b>Doc Pabean</b> berisi yang sudah dikirim, <b>Respon</b> menampilkan jawaban dari Bea Cukai, dan <b>Monitoring Blokir</b> memantau dokumen yang tertahan.',
  'tours.sidebar-map.kuota.title': 'Kuota Impor / Ekspor',
  'tours.sidebar-map.kuota.body':
    'Memantau kuota pengiriman dan harga referensi. Berguna kalau perusahaan Anda punya batas volume impor yang harus dijaga.',
  'tours.sidebar-map.cukai.title': 'Cukai',
  'tours.sidebar-map.cukai.body':
    'Khusus barang kena cukai (CK) — surat jalan, dokumen CK, bill of material, dan inventori. Hanya relevan kalau perusahaan Anda memegang izin cukai.',
  'tours.sidebar-map.customerApproval.title': 'Customer Approval',
  'tours.sidebar-map.customerApproval.body':
    'Kalau dokumen Anda perlu disetujui pelanggan sebelum dikirim ke Bea Cukai, alurnya diatur di sini — termasuk siapa peninjaunya dan ke grup email mana permintaan dikirim.',
  'tours.sidebar-map.creditTopup.title': 'Credit Topup',
  'tours.sidebar-map.creditTopup.body':
    'Pengiriman dokumen memakai kredit. Di sini Anda mengisi saldo dan melihat riwayat pemakaiannya.',
  'tours.sidebar-map.langganan.title': 'Langganan',
  'tours.sidebar-map.langganan.body': 'Paket berlangganan Jarvis milik perusahaan Anda dan masa berlakunya.',
  'tours.sidebar-map.berita.title': 'Berita',
  'tours.sidebar-map.berita.body':
    'Ringkasan berita ekonomi dan perdagangan dari beberapa sumber, supaya Anda tidak perlu keluar dari aplikasi.',
  'tours.sidebar-map.layananLainnya.title': 'Layanan Lainnya',
  'tours.sidebar-map.layananLainnya.body':
    'Alat bantu praktis: kompresi file, pemecah PDF, dan sejenisnya. Sering dipakai untuk merapikan dokumen sebelum diunggah.',
  'tours.sidebar-map.master.title': 'Master',
  'tours.sidebar-map.master.body':
    'Data induk: pengguna, akun CEISA, email, dan pengaturan lain. Umumnya hanya bisa diubah oleh admin perusahaan.',
  'tours.sidebar-map.preferences.title': 'Bahasa dan tampilan',
  'tours.sidebar-map.preferences.body':
    'Klik akun Anda di bagian bawah menu untuk mengganti bahasa. Tema tampilan diatur di <b>Pengaturan</b>. Pilihan ini tersimpan untuk kunjungan berikutnya.',

  // ---- buat-aju ---------------------------------------------------------
  'tours.buat-aju.intro.title': 'Pilih jenis dokumen',
  'tours.buat-aju.intro.body':
    'Setiap dokumen kepabeanan punya kode BC sendiri. Yang Anda pilih di sini menentukan formulir dan aturan validasi yang dipakai seterusnya, jadi pilih dengan benar sejak awal.',
  'tours.buat-aju.export.title': 'Ekspor — BC 3.0',
  'tours.buat-aju.export.body':
    'PEB (Pemberitahuan Ekspor Barang), untuk barang yang keluar dari Indonesia. Alurnya mirip PIB tetapi datanya berbeda.',
  'tours.buat-aju.ppftz.title': 'PPFTZ',
  'tours.buat-aju.ppftz.body':
    'Untuk lalu lintas barang dari dan ke kawasan bebas seperti Batam. Pilih ini hanya kalau lokasi Anda memang kawasan bebas.',
  'tours.buat-aju.import.title': 'Impor — BC 2.0 (PIB)',
  'tours.buat-aju.import.body':
    'Inilah yang kita pelajari: <b>PIB</b>, Pemberitahuan Impor Barang. Dokumen yang Anda sampaikan ke Bea Cukai untuk barang yang masuk ke Indonesia.<br /><br />Klik kartu ini untuk mulai.',

  // ---- doc-pabean-upload ------------------------------------------------
  'tours.doc-pabean-upload.intro.title': 'Mulai dari dokumen asli',
  'tours.doc-pabean-upload.intro.body':
    'Jarvis tidak meminta Anda mengetik ulang semuanya. Unggah dokumen dari pemasok — invoice, packing list, B/L — lalu Jarvis membaca isinya dan mengisi formulir PIB untuk Anda.',
  'tours.doc-pabean-upload.folderDropAll.title': 'Seret semua sekaligus',
  'tours.doc-pabean-upload.folderDropAll.body':
    'Punya setumpuk berkas? Jatuhkan semuanya di sini — atau di mana saja di halaman ini. JARVIS membaca tiap berkas dan memasukkannya ke folder yang sesuai. Lalu <b>Anda cek</b> tiap dokumen di pratinjau sebelah kanan: klik <b>Berikutnya</b> sampai semua halaman dilihat, lalu centang <b>Sudah dicek</b>.',
  'tours.doc-pabean-upload.folderInvoice.title': 'Folder menentukan jenis dokumen',
  'tours.doc-pabean-upload.folderInvoice.body':
    'Berkas yang Anda jatuhkan di folder <b>Invoice</b> dibaca sebagai Invoice — semua halamannya, termasuk halaman yang memuat biaya seperti "Ocean Freight". JARVIS hanya <b>mengingatkan</b> kalau isinya terlihat lain; ia tidak pernah mengganti jenisnya diam-diam.<br /><br />Salah folder? Seret berkas ke folder lain, atau ganti lewat tombol jenis dokumen di barisnya.',
  'tours.doc-pabean-upload.folderChecklist.title': 'Kelengkapan',
  'tours.doc-pabean-upload.folderChecklist.body':
    'PIB butuh <b>BL / AWB</b> dan <b>Invoice</b>. Daftar ini menunjukkan apa yang masih kurang dan peringatan mana yang belum dijawab. Tombolnya aktif begitu semuanya beres — berikutnya Anda memeriksa jenis dan halaman tiap dokumen di Review.',
  'tours.doc-pabean-upload.dropzone.gate': 'Unggah minimal satu berkas dulu untuk melanjutkan.',
  'tours.doc-pabean-upload.dropzone.title': 'Unggah berkas',
  'tours.doc-pabean-upload.dropzone.body':
    'Seret berkas PDF ke area ini, atau klik untuk memilih. Anda boleh mengunggah beberapa dokumen sekaligus.',
  'tours.doc-pabean-upload.docType.gate': 'Masih ada berkas yang belum diberi jenis dokumen. Lengkapi dulu semuanya.',
  'tours.doc-pabean-upload.docType.title': 'Tentukan jenis tiap dokumen',
  'tours.doc-pabean-upload.docType.body':
    'Setiap berkas harus diberi jenis: Invoice, Packing List, Bill of Lading, COO, dan seterusnya. Jarvis menebak otomatis, tetapi <b>periksa tebakannya</b> — jenis yang salah membuat data masuk ke kolom yang salah.',
  'tours.doc-pabean-upload.autoSplit.title': 'Pisah otomatis — jangan dilewati',
  'tours.doc-pabean-upload.autoSplit.body':
    'Satu berkas PDF sering berisi <b>beberapa dokumen sekaligus</b> — misalnya invoice dan packing list yang discan jadi satu. Tombol ini membaca isi PDF dan memisahkannya per dokumen.<br /><br />Pemisahan yang benar penting: Jarvis membaca tiap dokumen sesuai jenisnya, jadi pastikan jumlah dokumen di sini cocok dengan isi berkas aslinya.<br /><br />Langkah-langkah berikutnya menjelaskan cara memisahkannya sendiri kalau hasil otomatisnya belum pas.',
  'tours.doc-pabean-upload.preview.title': 'Buka berkas untuk dipratinjau',
  'tours.doc-pabean-upload.preview.body':
    '<b>Klik salah satu berkas PDF di daftar ini</b> untuk membukanya di panel sebelah kanan.<br /><br />Begitu terbuka, panduan lanjut ke cara memisahkan satu berkas menjadi beberapa dokumen.',
  'tours.doc-pabean-upload.reset.title': 'Salah unggah?',
  'tours.doc-pabean-upload.reset.body': 'Tombol ini mengosongkan daftar sehingga Anda bisa mulai dari awal.',
  'tours.doc-pabean-upload.process.title': 'Proses dokumen',
  'tours.doc-pabean-upload.process.body':
    'Tombol ini berubah mengikuti tahapan: <b>Proses</b> untuk membaca dokumen, lalu <b>Upload</b>, lalu <b>Lanjut Review</b>. Pembacaan bisa memakan waktu satu-dua menit untuk dokumen tebal.<br /><br />Klik untuk melanjutkan.',

  // ---- doc-pabean-upload: splitting one PDF into several documents ------
  'tours.doc-pabean-upload.splitIntro.title': 'Memisahkan satu berkas jadi beberapa dokumen',
  'tours.doc-pabean-upload.splitIntro.body':
    'Panel ini menentukan <b>berapa dokumen</b> yang dibaca Jarvis dari satu berkas PDF. Satu segmen = satu dokumen.<br /><br />Kalau hasil <b>Pisah otomatis</b> sudah benar, Anda tidak perlu mengubah apa pun. Panduan ini untuk saat hasilnya belum pas, atau saat Anda ingin memisahkan sendiri.',
  'tours.doc-pabean-upload.segmentAdd.gate': 'Klik Tambah Segment satu kali — panduan lanjut sendiri setelah itu.',
  'tours.doc-pabean-upload.segmentAdd.title': 'Langkah 1 — buat segmen',
  'tours.doc-pabean-upload.segmentAdd.body':
    'Klik <b>Tambah Segment</b> <b>satu kali</b> untuk membuat segmen pertama. Panduan langsung lanjut begitu segmennya muncul — belum perlu menambah segmen lain sekarang.<br /><br />Segmen yang baru dibuat menjadi segmen <b>aktif</b>: halaman yang Anda pilih berikutnya akan masuk ke sana. Nanti setelah segmen ini terisi, Anda bisa menambah segmen lagi untuk dokumen berikutnya di dalam berkas yang sama.',
  'tours.doc-pabean-upload.segmentPages.gate':
    'Masih ada segmen yang kosong. Pilih halaman untuk segmen itu, atau hapus segmennya lewat ikon tempat sampah di sebelah kanannya.',
  'tours.doc-pabean-upload.segmentPages.title': 'Langkah 2 — pilih halamannya',
  'tours.doc-pabean-upload.segmentPages.body':
    'Klik thumbnail halaman untuk memasukkannya ke segmen yang sedang aktif. Klik lagi untuk mengeluarkannya. Setiap halaman hanya boleh masuk ke satu segmen.<br /><br />Kalau berkas ini berisi lebih dari satu dokumen, ulangi polanya: <b>Tambah Segment</b> lagi, lalu pilih halaman untuk segmen itu — misalnya halaman 1–2 invoice, halaman 3 packing list.<br /><br />Segmen yang dibuat tapi dibiarkan kosong akan menahan proses; hapus saja kalau tidak jadi dipakai.',
  'tours.doc-pabean-upload.segmentRow.title': 'Langkah 3 — periksa isi segmen',
  'tours.doc-pabean-upload.segmentRow.body':
    'Setiap baris menampilkan halaman yang sudah masuk ke segmen itu. Klik barisnya untuk menjadikannya segmen aktif sebelum memilih halaman lagi.<br /><br />Pesan <i>"Setiap segmen harus memiliki minimal 1 halaman"</i> berarti masih ada segmen kosong — isi atau hapus dulu, karena tombol ekstrak belum bisa dipakai selama itu muncul.',
  'tours.doc-pabean-upload.segmentType.title': 'Langkah 4 — beri jenis dokumen',
  'tours.doc-pabean-upload.segmentType.body':
    'Pilih <b>Jenis dokumen</b> untuk tiap segmen: Invoice, Packing List, Bill of Lading, COO, dan seterusnya.<br /><br />Inilah yang menentukan ke kolom PIB mana isinya nanti dibaca. Segmen tanpa jenis yang benar akan terbaca ke tempat yang salah.',
  'tours.doc-pabean-upload.segmentOriginal.title': 'Langkah 5 — berkas aslinya diapakan?',
  'tours.doc-pabean-upload.segmentOriginal.body':
    'Kalau <b>Hapus file asli</b> dicentang, berkas gabungan tadi diganti oleh hasil pecahannya. Biarkan tercentang untuk kondisi normal — kalau tidak, berkas gabungan ikut terproses dan dokumen Anda terhitung dobel.',
  'tours.doc-pabean-upload.segmentExtract.title': 'Langkah 6 — ekstrak',
  'tours.doc-pabean-upload.segmentExtract.body':
    'Tombol ini memecah berkas sesuai segmen yang Anda susun. Setelah ini setiap segmen muncul sebagai berkas tersendiri di daftar kiri, dan Anda tinggal memastikan jenis dokumennya sudah benar.',

  // ---- doc-pabean-upload-export (PEB) -----------------------------------
  'tours.doc-pabean-upload-export.intro.title': 'Unggah dokumen ekspor',
  'tours.doc-pabean-upload-export.intro.body':
    'Alurnya sama seperti impor: unggah dokumen yang Anda punya, Jarvis membaca isinya, lalu hasilnya masuk ke formulir <b>PEB</b> (BC 3.0).<br /><br />Bedanya ada di jenis dokumen dan isian formulirnya nanti — ini pemberitahuan barang <b>keluar</b> dari Indonesia.',
  'tours.doc-pabean-upload-export.dropzone.title': 'Unggah berkas',
  'tours.doc-pabean-upload-export.dropzone.body':
    'Seret berkas PDF ke area ini atau klik untuk memilih. Untuk ekspor biasanya invoice, packing list, dan dokumen pengangkutan.',
  'tours.doc-pabean-upload-export.dropzone.gate': 'Unggah minimal satu berkas dulu untuk melanjutkan.',
  'tours.doc-pabean-upload-export.docType.title': 'Tentukan jenis tiap dokumen',
  'tours.doc-pabean-upload-export.docType.body':
    'Setiap berkas harus diberi jenis. Jarvis menebak otomatis — periksa tebakannya, karena jenis yang salah membuat isinya terbaca ke kolom yang salah. Instruksi pengiriman dari forwarder diberi jenis 000 - Shipping Instruction, bukan AWB.',
  'tours.doc-pabean-upload-export.docType.gate':
    'Masih ada berkas yang belum diberi jenis dokumen. Lengkapi dulu semuanya.',
  'tours.doc-pabean-upload-export.autoSplit.title': 'Pisah otomatis',
  'tours.doc-pabean-upload-export.autoSplit.body':
    'Kalau satu PDF berisi beberapa dokumen sekaligus, tombol ini membacanya dan memisahkan per dokumen. Pastikan jumlah dokumen hasil pemisahan cocok dengan isi berkas aslinya.',
  'tours.doc-pabean-upload-export.reset.title': 'Salah unggah?',
  'tours.doc-pabean-upload-export.reset.body': 'Tombol ini mengosongkan daftar sehingga Anda bisa mulai dari awal.',
  'tours.doc-pabean-upload-export.process.title': 'Proses dokumen',
  'tours.doc-pabean-upload-export.process.body':
    'Tombol ini berubah mengikuti tahapan: <b>Proses</b>, lalu <b>Upload</b>, lalu lanjut ke formulir PEB.<br /><br />Klik untuk melanjutkan.',

  // ---- export-form (PEB) -------------------------------------------------
  'tours.export-form.intro.title': 'Formulir PEB (BC 3.0)',
  'tours.export-form.intro.body':
    'Ini formulir <b>Pemberitahuan Ekspor Barang</b> — dokumen untuk barang yang keluar dari Indonesia. Jarvis sudah mengisi sebagian dari dokumen yang Anda unggah; periksa hasilnya, perbaiki yang keliru, dan lengkapi yang kosong.<br /><br />Panduan ini mengenalkan setiap tab dan tombol pengirimannya.',
  'tours.export-form.actions.title': 'Menu Aksi',
  'tours.export-form.actions.body':
    'Di sini tersedia tindakan lain seperti cetak formulir dan revisi. Membaca hasil cetaknya sebelum mengirim adalah cara yang baik untuk menangkap kesalahan yang luput di layar.',
  'tours.export-form.submit.title': 'Nomor Aju & Submit',
  'tours.export-form.submit.body':
    'Nomor Aju PEB tampil di sini — bisa diubah atau diambil ulang lewat ikon di sampingnya. Tombol Submit Draft mengirim PEB ke CEISA. Pastikan seluruh tab sudah bersih dari error sebelum menekannya — setelah terkirim, perbaikan dilakukan lewat revisi dari menu Aksi.',

  // ---- PEB form tabs (fields verified against src/app/export/forms) ------
  'tours.export-form.tabs.title': 'Delapan tab',
  'tours.export-form.tabs.body':
    'Data PEB dibagi ke delapan tab. Tab yang ditandai <b>merah</b> berarti masih ada yang salah atau kurang.<br /><br />Tidak ada tab Pungutan tersendiri seperti di PIB — untuk ekspor, <b>Bea Keluar</b>, <b>PPh 22</b>, dan <b>Pungutan Sawit</b> ada di dalam tab Transaksi.<br /><br />Setiap tab punya panduannya sendiri: buka sebuah tab dan panduannya muncul saat pertama kali dibuka.',

  // ---- PEB form tabs (anchors + copy follow src/app/export/forms) --------
  'tours.peb-tab-header.intro.title': 'Tab Header',
  'tours.peb-tab-header.intro.body':
    'Identitas dasar dokumen ekspor: dari mana barang dimuat, kantor pabean mana yang menangani, dan ekspor jenis apa ini.',
  'tours.peb-tab-header.pelabuhan.title': 'Pelabuhan Muat Ekspor',
  'tours.peb-tab-header.pelabuhan.body':
    'Pelabuhan tempat barang dimuat untuk diekspor. Cocokkan dengan dokumen pengangkutan Anda.',
  'tours.peb-tab-header.kantor.title': 'Kantor Pabean Muat Asal',
  'tours.peb-tab-header.kantor.body':
    'Kantor pabean di tempat barang mulai dimuat. Sebagian terisi otomatis dari pelabuhan yang Anda pilih — periksa hasilnya.',
  'tours.peb-tab-header.kantorEkspor.title': 'Kantor Pabean Muat Ekspor',
  'tours.peb-tab-header.kantorEkspor.body':
    'Kantor pabean di tempat barang benar-benar diekspor. Sama dengan kantor muat asal kalau barang tidak berpindah dulu.',
  'tours.peb-tab-header.jenisEkspor.title': 'Jenis Ekspor',
  'tours.peb-tab-header.jenisEkspor.body':
    'Skema ekspor untuk pengiriman ini. Bawaannya <b>1 - EKSPOR BIASA</b>; pilihan lain dipakai untuk skema tertentu.',
  'tours.peb-tab-header.kategori.title': 'Kategori Ekspor',
  'tours.peb-tab-header.kategori.body':
    'Kategori pengiriman, misalnya <b>10 - UMUM</b>. Pilih sesuai skema perusahaan Anda; kalau ragu, tanyakan ke tim kepabeanan Anda.',
  'tours.peb-tab-header.caraDagang.title': 'Cara Dagang',
  'tours.peb-tab-header.caraDagang.body':
    'Syarat perdagangan yang disepakati dengan pembeli, misalnya <b>1 - BIASA</b>. Cocokkan dengan kontrak atau invoice Anda.',
  'tours.peb-tab-header.caraBayar.title': 'Cara Bayar',
  'tours.peb-tab-header.caraBayar.body':
    'Cara pembayaran untuk transaksi ini, misalnya <b>1 - BIASA/TUNAI</b>. Kolom <b>Keterangan Pembayaran</b> di sebelahnya untuk catatan tambahan.',
  'tours.peb-tab-header.komoditi.title': 'Komoditi',
  'tours.peb-tab-header.komoditi.body':
    'Pilihannya <b>1 - MIGAS</b> atau <b>2 - NON MIGAS</b> — apakah barang termasuk minyak dan gas.',
  'tours.peb-tab-header.curah.title': 'Curah',
  'tours.peb-tab-header.curah.body': 'Menandai apakah barang dikirim dalam bentuk curah (tanpa kemasan satuan).',
  'tours.peb-tab-header.barangKiriman.title': 'Ekspor Barang Kiriman',
  'tours.peb-tab-header.barangKiriman.body':
    'Menandai pengiriman lewat penyelenggara pos atau kurir. Bawaannya <b>T - BUKAN EKSPOR BARANG KIRIMAN</b>.',
  'tours.peb-tab-header.next.title': 'Lanjut ke Entitas',
  'tours.peb-tab-header.next.body':
    'Klik <b>Selanjutnya</b>. Panduan tab berikutnya muncul saat tab itu pertama kali dibuka.',

  'tours.peb-tab-entitas.intro.title': 'Tab Entitas',
  'tours.peb-tab-entitas.intro.body':
    'Pihak-pihak dalam ekspor ini, dimulai dari <b>NPWP Eksportir</b>. Perhatikan bahwa penerima dan pembeli dibedakan.',
  'tours.peb-tab-entitas.eksportir.title': 'Nama Eksportir',
  'tours.peb-tab-entitas.eksportir.body': 'Nama perusahaan pengekspor. Cocokkan dengan dokumen resmi perusahaan.',
  'tours.peb-tab-entitas.alamatEksportir.title': 'Alamat Eksportir',
  'tours.peb-tab-entitas.alamatEksportir.body': 'Alamat terdaftar eksportir, sesuai dokumen resminya.',
  'tours.peb-tab-entitas.penerima.title': 'Nama Penerima',
  'tours.peb-tab-entitas.penerima.body':
    'Pihak yang <b>menerima</b> barang di luar negeri, sesuai dokumen pengangkutan.',
  'tours.peb-tab-entitas.negaraPenerima.title': 'Negara Penerima',
  'tours.peb-tab-entitas.negaraPenerima.body': 'Negara tempat penerima berada. Bisa berbeda dari negara pembeli.',
  'tours.peb-tab-entitas.pembeli.title': 'Nama Pembeli',
  'tours.peb-tab-entitas.pembeli.body':
    'Pihak yang <b>membeli</b> barang — sering berbeda dari penerima. Isi sesuai invoice.',
  'tours.peb-tab-entitas.negaraPembeli.title': 'Negara Pembeli',
  'tours.peb-tab-entitas.negaraPembeli.body':
    'Negara pembeli. Kalau berbeda dari negara penerima, keduanya tetap dilaporkan terpisah.',
  'tours.peb-tab-entitas.ppjk.title': 'PPJK',
  'tours.peb-tab-entitas.ppjk.body':
    'Isi bagian ini kalau pengurusan diwakilkan ke jasa kepabeanan. Ada juga <b>Nomor Identitas PPJK</b> dan <b>Alamat PPJK</b> di dekatnya.',
  'tours.peb-tab-entitas.konsolidasi.title': 'Lokasi Konsolidasi',
  'tours.peb-tab-entitas.konsolidasi.body': 'Diisi bila barang digabung dengan kiriman lain sebelum dimuat.',
  'tours.peb-tab-entitas.next.title': 'Lanjut ke Document',
  'tours.peb-tab-entitas.next.body': 'Klik <b>Selanjutnya</b> untuk pindah ke tab berikutnya.',

  'tours.peb-tab-document.intro.title': 'Tab Document',
  'tours.peb-tab-document.intro.body': 'Dokumen pendukung ekspor beserta nomor dan tanggalnya.',
  'tours.peb-tab-document.daftar.title': 'Daftar dokumen',
  'tours.peb-tab-document.daftar.body':
    'Tambah, ubah, salin, atau hapus baris di sini. Periksa nomor dan tanggalnya terhadap berkas aslinya. Sebuah dokumen juga bisa dikaitkan ke barang tertentu lewat <b>Lampirkan Dokumen ke Barang</b>.',
  'tours.peb-tab-document.next.title': 'Lanjut ke Pengangkut',
  'tours.peb-tab-document.next.body': 'Klik <b>Selanjutnya</b> untuk pindah ke tab berikutnya.',

  'tours.peb-tab-pengangkut.intro.title': 'Pelabuhan muat asal',
  'tours.peb-tab-pengangkut.intro.body':
    'Tab Pengangkut menjelaskan perjalanan barang keluar. Dimulai dari pelabuhan tempat barang mula-mula dimuat.',
  'tours.peb-tab-pengangkut.muatEkspor.title': 'Pelabuhan muat Ekspor',
  'tours.peb-tab-pengangkut.muatEkspor.body':
    'Pelabuhan tempat barang benar-benar diekspor. Sama dengan muat asal kalau barang tidak berpindah dulu.',
  'tours.peb-tab-pengangkut.penimbunan.title': 'Tempat penimbunan',
  'tours.peb-tab-pengangkut.penimbunan.body': 'Tempat barang ditimbun sebelum dimuat.',
  'tours.peb-tab-pengangkut.tanggal.title': 'Tanggal perkiraan ekspor',
  'tours.peb-tab-pengangkut.tanggal.body': 'Perkiraan tanggal barang diekspor, sesuai jadwal pengangkutan Anda.',
  'tours.peb-tab-pengangkut.sarana.title': 'Jenis Pengangkutan',
  'tours.peb-tab-pengangkut.sarana.body':
    'Moda pengangkutan yang membawa barang keluar. Salin dari dokumen pengangkutan.',
  'tours.peb-tab-pengangkut.tujuan.title': 'Pelabuhan tujuan',
  'tours.peb-tab-pengangkut.tujuan.body': 'Pelabuhan tujuan akhir barang.',
  'tours.peb-tab-pengangkut.negara.title': 'Negara tujuan ekspor',
  'tours.peb-tab-pengangkut.negara.body': 'Negara tujuan barang. Cocokkan dengan invoice dan dokumen pengangkutan.',
  'tours.peb-tab-pengangkut.pemeriksaan.title': 'Pemeriksaan',
  'tours.peb-tab-pengangkut.pemeriksaan.body':
    '<b>Lokasi Pemeriksaan</b>, dan di dekatnya <b>Kantor Pabean Pemeriksaan</b> serta <b>Tanggal periksa</b> — dipakai bila barang dijadwalkan diperiksa sebelum dimuat.',
  'tours.peb-tab-pengangkut.next.title': 'Lanjut ke Kemasan',
  'tours.peb-tab-pengangkut.next.body': 'Klik <b>Selanjutnya</b> untuk pindah ke tab berikutnya.',

  'tours.peb-tab-kemasan.intro.title': 'Tab Kemasan & Peti Kemas',
  'tours.peb-tab-kemasan.intro.body': 'Berapa banyak kemasan yang dikirim, dan di peti kemas mana barangnya berada.',
  'tours.peb-tab-kemasan.kemasan.title': 'Daftar kemasan',
  'tours.peb-tab-kemasan.kemasan.body':
    'Tambah dan ubah baris di sini. Cocokkan jumlahnya dengan Packing List, dan dengan berat yang Anda isi di tab Transaksi.',
  'tours.peb-tab-kemasan.next.title': 'Lanjut ke Transaksi',
  'tours.peb-tab-kemasan.next.body': 'Klik <b>Selanjutnya</b> untuk pindah ke tab berikutnya.',

  'tours.peb-tab-transaksi.intro.title': 'Valuta',
  'tours.peb-tab-transaksi.intro.body':
    'Tab Transaksi adalah sisi keuangan ekspor ini — dan tempat pungutan ekspor dihitung. Mulai dari mata uang transaksinya.',
  'tours.peb-tab-transaksi.ndpbm.title': 'NDPBM',
  'tours.peb-tab-transaksi.ndpbm.body':
    'Nilai dasar yang dipakai untuk mengonversi valuta di atas. Kalau Anda tidak yakin angka mana yang berlaku, tanyakan ke tim kepabeanan Anda.',
  'tours.peb-tab-transaksi.penyerahan.title': 'Cara penyerahan',
  'tours.peb-tab-transaksi.penyerahan.body':
    'Syarat penyerahan yang disepakati dengan pembeli. Cocokkan dengan kontrak atau invoice Anda.',
  'tours.peb-tab-transaksi.nilai.title': 'Nilai Ekspor (Incoterm FOB)',
  'tours.peb-tab-transaksi.nilai.body':
    'Nilai barang yang diekspor, diisi dalam basis <b>FOB</b> seperti tertulis pada label kolomnya.',
  'tours.peb-tab-transaksi.asuransi.title': 'Asuransi',
  'tours.peb-tab-transaksi.asuransi.body': 'Nilai asuransi untuk pengiriman ini, beserta jenis asuransinya.',
  'tours.peb-tab-transaksi.maklon.title': 'Nilai Maklon',
  'tours.peb-tab-transaksi.maklon.body':
    'Diisi untuk pekerjaan makloon — barang milik pihak lain yang Anda proses lalu diekspor kembali.',
  'tours.peb-tab-transaksi.beratKotor.title': 'Berat Kotor (KGM)',
  'tours.peb-tab-transaksi.beratKotor.body': 'Berat termasuk kemasan, dalam kilogram. Cocokkan dengan Packing List.',
  'tours.peb-tab-transaksi.beratBersih.title': 'Berat Bersih (KGM)',
  'tours.peb-tab-transaksi.beratBersih.body':
    'Berat barang saja, dalam kilogram. Di tab ini juga ada panel <b>Info Bea Keluar</b>, <b>Info PPh 22</b>, dan <b>Info Pungutan Sawit</b> beserta <b>Preview Bea Keluar</b> — jalankan lagi setiap kali nilai atau barangnya berubah.',
  'tours.peb-tab-transaksi.next.title': 'Lanjut ke Barang',
  'tours.peb-tab-transaksi.next.body': 'Klik <b>Selanjutnya</b> untuk pindah ke tab berikutnya.',

  'tours.peb-tab-barang.intro.title': 'Tab Barang',
  'tours.peb-tab-barang.intro.body': 'Setiap jenis barang yang diekspor didaftarkan sebagai satu baris.',
  'tours.peb-tab-barang.baris.title': 'Daftar barang',
  'tours.peb-tab-barang.baris.body':
    'Jumlah barisnya harus sesuai dengan invoice dan Packing List. Tiap baris memuat HS Code, jumlah, satuan, dan berat; kotak pencarian mencari berdasarkan uraian atau HS Code.',
  'tours.peb-tab-barang.next.title': 'Lanjut ke Pernyataan',
  'tours.peb-tab-barang.next.body': 'Klik <b>Selanjutnya</b> untuk sampai ke tab terakhir.',

  'tours.peb-tab-pernyataan.intro.title': 'Nama',
  'tours.peb-tab-pernyataan.intro.body': 'Tab terakhir. Nama penanggung jawab atas pemberitahuan ekspor ini.',
  'tours.peb-tab-pernyataan.jabatan.title': 'Jabatan',
  'tours.peb-tab-pernyataan.jabatan.body': 'Jabatan orang tersebut di perusahaan.',
  'tours.peb-tab-pernyataan.tempat.title': 'Tempat',
  'tours.peb-tab-pernyataan.tempat.body': 'Tempat pemberitahuan ini dibuat.',
  'tours.peb-tab-pernyataan.tanggal.title': 'Tanggal',
  'tours.peb-tab-pernyataan.tanggal.body': 'Tanggal pembuatan pemberitahuan.',
  'tours.peb-tab-pernyataan.submit.title': 'Nomor Aju & Submit',
  'tours.peb-tab-pernyataan.submit.body':
    'Nomor Aju PEB tampil di sini — bisa diubah atau diambil ulang lewat ikon di sampingnya. Tombol Submit Draft mengirim PEB ke CEISA. Pastikan seluruh tab sudah bersih dari error sebelum menekannya — setelah terkirim, perbaikan dilakukan lewat revisi dari menu Aksi.',

  // ---- doc-pabean-draft-list --------------------------------------------
  'tours.doc-pabean-draft-list.intro.title': 'Daftar draft',
  'tours.doc-pabean-draft-list.intro.body':
    'Draft adalah dokumen yang <b>belum dikirim</b> ke Bea Cukai — apa pun masih boleh diubah. Centang beberapa baris untuk menghapusnya sekaligus.',
  'tours.doc-pabean-draft-list.search.title': 'Cari draft',
  'tours.doc-pabean-draft-list.search.body':
    'Cari berdasarkan nomor aju, nama importir, atau nomor invoice. Berguna sekali begitu draft Anda mulai banyak.',
  'tours.doc-pabean-draft-list.open.title': 'Buka draft',
  'tours.doc-pabean-draft-list.open.body':
    'Klik salah satu baris untuk membuka formulir PIB-nya. Di sanalah Anda memeriksa hasil pembacaan Jarvis dan melengkapi yang kurang.<br /><br />Klik sebuah draft untuk melanjutkan.',

  // ---- manifest-from-pabean ---------------------------------------------
  'tours.manifest-from-pabean.note.title': 'Hanya PEB dari JARVIS',
  'tours.manifest-from-pabean.note.body':
    'Daftar ini hanya berisi PEB yang dibuat lewat JARVIS dan sudah mendapat respon <b>NPE</b>. PEB yang dibuat di luar JARVIS bisa ditambahkan nanti dari dalam draft lewat <b>Upload PEB Susulan</b>.',
  'tours.manifest-from-pabean.table.title': 'Centang PEB yang dimuat bersama',
  'tours.manifest-from-pabean.table.body':
    'Satu PEB menjadi satu house BL/AWB. <b>Urutan centang</b> menjadi urutan pos di manifest, dan header manifest mengikuti PEB pertama. PEB berlabel "Sudah di draft" tetap bisa dipilih.',
  'tours.manifest-from-pabean.create.title': 'Buat Manifest',
  'tours.manifest-from-pabean.create.body':
    'Draft dibuat dari data PEB — tanpa upload dan tanpa ekstraksi AI — lalu dibuka di editor. Master BL boleh diisi belakangan.',

  // ---- manifest-from-pabean-inward --------------------------------------
  'tours.manifest-from-pabean-inward.note.title': 'Hanya PIB dari JARVIS',
  'tours.manifest-from-pabean-inward.note.body':
    'Daftar ini hanya berisi PIB yang dibuat lewat JARVIS dan sudah mendapat respon <b>SPPB</b>. House BL/AWB dari PIB di luar JARVIS bisa ditambahkan nanti dari dalam draft lewat <b>Upload BL/AWB Susulan</b>.',
  'tours.manifest-from-pabean-inward.table.title': 'Centang PIB yang dikonsolidasikan bersama',
  'tours.manifest-from-pabean-inward.table.body':
    'Satu PIB menjadi satu house BL/AWB. <b>Urutan centang</b> menjadi urutan pos di manifest, dan header manifest mengikuti PIB pertama. Kolom BC 1.1 menunjukkan manifest asal pengangkut yang dirujuk PIB.',
  'tours.manifest-from-pabean-inward.create.title': 'Buat Manifest',
  'tours.manifest-from-pabean-inward.create.body':
    'Draft inward dibuat dari data PIB — tanpa upload dan tanpa ekstraksi AI — lalu dibuka di editor. Master BL/AWB boleh diisi belakangan.',

  // ---- doc-pabean-list --------------------------------------------------
  'tours.doc-pabean-list.intro.title': 'Dokumen yang sudah dikirim',
  'tours.doc-pabean-list.intro.body':
    'Berbeda dengan Draft, halaman ini berisi dokumen yang <b>sudah</b> dikirim ke CEISA. Di sini Anda memantau perjalanannya.',
  'tours.doc-pabean-list.credits.title': 'Saldo kredit',
  'tours.doc-pabean-list.credits.body':
    'Pengiriman dokumen memakai kredit. Pantau sisanya di sini, dan isi ulang lewat menu Topup Kredit sebelum saldo menipis.',
  'tours.doc-pabean-list.filters.title': 'Saring dan urutkan',
  'tours.doc-pabean-list.filters.body':
    'Saring berdasarkan status, tanggal, atau cabang. Kalau perusahaan Anda punya beberapa cabang, saringan cabang membatasi tampilan pada milik Anda saja.',
  'tours.doc-pabean-list.status.title': 'Baca kolom status',
  'tours.doc-pabean-list.status.body':
    'Status menunjukkan posisi dokumen di Bea Cukai. Perhatikan terutama dokumen yang <b>ditolak</b> atau <b>diblokir</b> — keduanya perlu ditindaklanjuti, dan menu Aksi pada dokumen menyediakan pilihan <b>Revisi</b> untuk mengirim ulang perbaikannya.',
  'tours.doc-pabean-list.report.title': 'Unduh laporan',
  'tours.doc-pabean-list.report.body':
    'Menghasilkan rekap dokumen dalam rentang tanggal tertentu, untuk keperluan pelaporan internal atau audit.',

  // ---- pib-edit-overview ------------------------------------------------
  'tours.pib-edit-overview.intro.title': 'Formulir PIB',
  'tours.pib-edit-overview.intro.body':
    'Inilah inti pekerjaan Anda. Jarvis sudah mengisi sebagian kolom dari dokumen yang Anda unggah — <b>periksa hasilnya, perbaiki yang keliru, dan lengkapi yang masih kosong</b>.<br /><br />Dokumen asli tampil di sisi kiri supaya bisa dibandingkan langsung.',
  'tours.pib-edit-overview.errors.title': 'Daftar kesalahan',
  'tours.pib-edit-overview.errors.body':
    'Panel merah ini mencantumkan semua yang masih menghalangi pengiriman. Klik sebuah kesalahan untuk melompat langsung ke kolomnya.<br /><br />Ada juga <b>Bot Assist</b> yang memandu perbaikan satu per satu — sangat membantu saat kesalahannya banyak.',
  'tours.pib-edit-overview.toolbar.title': 'Alat bantu',
  'tours.pib-edit-overview.toolbar.body':
    'Unduh data ke Excel, ubah di sana, lalu unggah kembali — cara tercepat kalau barangnya puluhan atau ratusan baris. Ada juga sinkronisasi dengan data historis untuk mengambil isian dari dokumen serupa yang lalu.',
  'tours.pib-edit-overview.actions.title': 'Validasi, cetak, kirim',
  'tours.pib-edit-overview.actions.body':
    'Urutan yang benar: <b>Validasi</b> untuk memastikan data diterima CEISA, lalu <b>Cetak Formulir Draft CEISA</b> untuk diperiksa manusia, baru <b>Kirim</b>.<br /><br />Setelah dikirim, perbaikan hanya bisa lewat revisi — jadi jangan lewati langkah cetak.',
  'tours.pib-edit-overview.footer.title': 'Berpindah antar tab',
  'tours.pib-edit-overview.footer.body':
    'Tombol Sebelumnya dan Selanjutnya menelusuri tab secara berurutan. Perpindahan tab otomatis menyimpan perubahan Anda — tidak ada tombol Simpan terpisah.',
  'tours.pib-edit-overview.tabs.title': 'Sembilan tab',
  'tours.pib-edit-overview.tabs.body':
    'Data PIB dibagi ke sembilan tab. Tab yang <b>merah</b> berarti masih ada yang salah atau kurang di dalamnya.<br /><br />Setiap tab punya panduannya sendiri: buka sebuah tab dan panduannya akan muncul otomatis saat pertama kali dibuka.',

  // ---- pib-tab-header ---------------------------------------------------
  'tours.pib-tab-header.intro.title': 'Tab Header',
  'tours.pib-tab-header.intro.body':
    'Identitas dasar dokumen: barang ini masuk lewat pelabuhan mana, diurus kantor pabean mana, dan untuk keperluan apa. Isian di sini menentukan aturan yang dipakai tab-tab berikutnya.',
  'tours.pib-tab-header.pelabuhan.title': 'Pelabuhan',
  'tours.pib-tab-header.pelabuhan.body':
    'Pelabuhan tempat barang dibongkar. Cocokkan dengan pelabuhan yang tertulis di Bill of Lading.',
  'tours.pib-tab-header.kantorPabean.title': 'Kantor Pabean',
  'tours.pib-tab-header.kantorPabean.body':
    'Kantor Bea Cukai yang menangani pemasukan barang. Kolom ini mengikuti pilihan pelabuhan secara otomatis — tapi kalau Anda mengganti pelabuhan, <b>periksa lagi kantornya</b>.',
  'tours.pib-tab-header.jenisPib.title': 'Jenis PIB',
  'tours.pib-tab-header.jenisPib.body':
    'Pilihannya <b>1 - BIASA</b> atau <b>2 - BERKALA</b>. Berkala dipakai untuk skema pemberitahuan berkala; kalau Anda tidak yakin perusahaan Anda memakainya, tanyakan dulu ke atasan atau tim kepabeanan Anda.',
  'tours.pib-tab-header.jenisImpor.title': 'Jenis Impor',
  'tours.pib-tab-header.jenisImpor.body':
    'Tujuan barang masuk: <b>1 - UNTUK DIPAKAI</b>, <b>2 - SEMENTARA</b>, <b>5 - RUSH HANDLING</b>, atau <b>9 - GABUNGAN 1 DAN 2</b>.<br /><br />Pilih sesuai keperluan impor Anda. Kalau ragu, cocokkan dengan dokumen impor sebelumnya untuk barang serupa.',
  'tours.pib-tab-header.caraBayar.title': 'Cara Bayar',
  'tours.pib-tab-header.caraBayar.body':
    'Cara pembayaran yang berlaku untuk transaksi ini. <b>1 - BIASA/TUNAI</b> dan <b>2 - BERKALA</b> yang paling sering dipakai; sisanya menyebut syarat pembayaran ke pemasok — Open Account, T/T, Advance Payment, Sight L/C, Inkaso, atau tanpa pembayaran.<br /><br />Cocokkan dengan syarat pembayaran yang tertulis di invoice atau kontrak Anda.',
  'tours.pib-tab-header.next.title': 'Lanjut ke Entitas',
  'tours.pib-tab-header.next.body':
    'Klik Selanjutnya. Perubahan Anda tersimpan otomatis, dan panduan tab berikutnya akan muncul.',

  // ---- pib-tab-entitas --------------------------------------------------
  'tours.pib-tab-entitas.intro.title': 'Tab Entitas',
  'tours.pib-tab-entitas.intro.body':
    'Pihak-pihak dalam impor ini, masing-masing dengan bagiannya sendiri: Importir, Pemilik Barang, Pengirim, Penjual, PPJK, dan Pemusatan. Setiap bagian dilaporkan terpisah.',
  'tours.pib-tab-entitas.importir.title': 'Importir',
  'tours.pib-tab-entitas.importir.body':
    'Perusahaan yang tercatat sebagai pengimpor, termasuk NPWP dan alamatnya. Cocokkan dengan dokumen resmi perusahaan tersebut.',
  'tours.pib-tab-entitas.pemilik.title': 'Pemilik Barang',
  'tours.pib-tab-entitas.pemilik.body':
    'Pihak yang benar-benar memiliki barang. Bisa sama dengan importir, bisa juga berbeda — misalnya kalau impor diurus atas nama pihak lain.',
  'tours.pib-tab-entitas.pengirim.title': 'Pengirim',
  'tours.pib-tab-entitas.pengirim.body': 'Pihak yang mengirim barang dari luar negeri, sesuai dokumen pengangkutan.',
  'tours.pib-tab-entitas.penjual.title': 'Penjual',
  'tours.pib-tab-entitas.penjual.body':
    'Pihak yang menjual barang. Sering sama dengan pengirim, tetapi tidak selalu — isi sesuai invoice.',
  'tours.pib-tab-entitas.pemusatan.title': 'Pemusatan',
  'tours.pib-tab-entitas.pemusatan.body':
    'Diisi bila perusahaan Anda memakai skema pemusatan. Kalau tidak, biarkan kosong.',
  'tours.pib-tab-entitas.ppjk.title': 'PPJK',
  'tours.pib-tab-entitas.ppjk.body':
    'Isi bagian ini kalau pengurusan kepabeanan diwakilkan. Kalau perusahaan mengurus sendiri, bagian ini boleh dikosongkan.',
  'tours.pib-tab-entitas.next.title': 'Lanjut ke Dokumen',
  'tours.pib-tab-entitas.next.body': 'Klik Selanjutnya untuk pindah ke tab Dokumen.',

  // ---- pib-tab-dokumen --------------------------------------------------
  'tours.pib-tab-dokumen.intro.title': 'Tab Dokumen',
  'tours.pib-tab-dokumen.intro.body':
    'Daftar dokumen pendukung beserta nomor dan tanggalnya. Ada juga saringan HS Code dan pengisian Seri Ijin massal di tab ini.',
  'tours.pib-tab-dokumen.wajib.title': 'Daftar dokumen',
  'tours.pib-tab-dokumen.wajib.body':
    'Periksa nomor dan tanggal tiap baris terhadap berkas aslinya. Tanggal pada dokumen asli kadang ditulis dengan tahun dua digit — pastikan yang tersimpan di sini tahunnya sudah benar.',
  'tours.pib-tab-dokumen.tambah.title': 'Menambah dokumen',
  'tours.pib-tab-dokumen.tambah.body':
    'Tombol ini membuka formulir untuk menambah dokumen yang belum terbaca otomatis.<br /><br />Klik untuk membukanya — panduan akan ditutup supaya tidak menghalangi.',

  // ---- pib-tab-pengangkut -----------------------------------------------
  'tours.pib-tab-pengangkut.intro.title': 'Tab Pengangkut',
  'tours.pib-tab-pengangkut.intro.body':
    'Data sarana yang membawa barang Anda. Bea Cukai memakai bagian ini untuk mencocokkan PIB Anda dengan manifes kapal yang sudah lebih dulu mereka terima.',
  'tours.pib-tab-pengangkut.sarana.title': 'Nama sarana dan voyage',
  'tours.pib-tab-pengangkut.sarana.body':
    'Nama kapal atau nomor penerbangan, beserta nomor voyage. Salin persis seperti tertulis di Bill of Lading, termasuk ejaannya.',
  'tours.pib-tab-pengangkut.bc11.title': 'Nomor BC 1.1',
  'tours.pib-tab-pengangkut.bc11.body':
    'BC 1.1 adalah manifes yang dilaporkan pengangkut ke Bea Cukai berisi seluruh muatan kapal. Nomor ini menghubungkan barang Anda ke manifes tersebut.',
  'tours.pib-tab-pengangkut.pos.title': 'Pos dan Sub Pos',
  'tours.pib-tab-pengangkut.pos.body':
    'Nomor urut barang Anda di dalam manifes BC 1.1. Kalau Anda tidak memilikinya, tanyakan ke pihak pelayaran atau forwarder yang mengurus pengiriman ini.',
  'tours.pib-tab-pengangkut.tanggalTutup.title': 'Tanggal Tutup PU',
  'tours.pib-tab-pengangkut.tanggalTutup.body':
    'Tanggal penutupan pemberitahuan umum untuk sarana pengangkut ini, sesuai data BC 1.1.',
  'tours.pib-tab-pengangkut.subpos.title': 'Nomor Subpos',
  'tours.pib-tab-pengangkut.subpos.body':
    'Perincian di bawah Nomor Pos. Isi kalau manifes yang Anda terima mencantumkannya.',
  'tours.pib-tab-pengangkut.subsubpos.title': 'Nomor Subsubpos',
  'tours.pib-tab-pengangkut.subsubpos.body':
    'Tingkat perincian paling dalam di bawah Subpos. Sering kosong — isi hanya kalau memang tercantum.',
  'tours.pib-tab-pengangkut.pelTujuan.title': 'Pelabuhan Tujuan',
  'tours.pib-tab-pengangkut.pelTujuan.body':
    'Pelabuhan tujuan barang. Ada juga <b>Pelabuhan Muat</b> dan <b>Bendera</b> sarana di formulir ini — cocokkan ketiganya dengan dokumen pengangkutan.',
  'tours.pib-tab-pengangkut.next.title': 'Lanjut ke Kemasan',
  'tours.pib-tab-pengangkut.next.body': 'Klik Selanjutnya untuk pindah ke tab Kemasan & Peti Kemas.',

  // ---- pib-tab-kemasan --------------------------------------------------
  'tours.pib-tab-kemasan.intro.title': 'Kemasan',
  'tours.pib-tab-kemasan.intro.body':
    'Tab ini punya dua daftar: <b>Kemasan</b> di atas dan <b>Peti Kemas</b> di bawahnya.',
  'tours.pib-tab-kemasan.jumlah.title': 'Daftar kemasan',
  'tours.pib-tab-kemasan.jumlah.body':
    'Jumlah dan jenis kemasan, misalnya "120 CARTON". Cocokkan dengan Packing List dan dengan berat yang Anda isi di tab Transaksi.',
  'tours.pib-tab-kemasan.petiKemas.title': 'Peti Kemas',
  'tours.pib-tab-kemasan.petiKemas.body': 'Nomor kontainer beserta ukuran dan jenisnya, sesuai Bill of Lading.',
  'tours.pib-tab-kemasan.next.title': 'Lanjut ke Transaksi',
  'tours.pib-tab-kemasan.next.body': 'Klik Selanjutnya untuk pindah ke tab Transaksi.',

  // ---- pib-tab-transaksi ------------------------------------------------
  'tours.pib-tab-transaksi.intro.title': 'Valuta',
  'tours.pib-tab-transaksi.intro.body':
    'Tab Transaksi adalah sisi keuangan impor ini. Mulai dari mata uang yang dipakai pada invoice.',
  'tours.pib-tab-transaksi.ndpbm.title': 'NDPBM',
  'tours.pib-tab-transaksi.ndpbm.body':
    'Nilai dasar yang dipakai untuk mengonversi valuta di atas. Kalau Anda tidak yakin angka mana yang berlaku, tanyakan ke tim kepabeanan Anda.',
  'tours.pib-tab-transaksi.jenisTransaksi.title': 'Jenis Transaksi',
  'tours.pib-tab-transaksi.jenisTransaksi.body':
    'Sifat transaksi pembelian ini. Cocokkan dengan kontrak atau invoice Anda.',
  'tours.pib-tab-transaksi.incoterm.title': 'Incoterm',
  'tours.pib-tab-transaksi.incoterm.body':
    'Syarat penyerahan yang disepakati dengan pemasok — FOB, CIF, CFR, dan seterusnya. Pilihan ini menentukan biaya mana yang sudah termasuk dalam harga invoice.',
  'tours.pib-tab-transaksi.hargaBarang.title': 'Harga Barang',
  'tours.pib-tab-transaksi.hargaBarang.body': 'Nilai barang sesuai invoice, dalam valuta yang Anda pilih di atas.',
  'tours.pib-tab-transaksi.asuransi.title': 'Asuransi',
  'tours.pib-tab-transaksi.asuransi.body':
    'Nilai asuransi untuk pengiriman ini. Apakah perlu diisi tergantung Incoterm yang Anda pilih — cocokkan dengan invoice dan polis Anda.',
  'tours.pib-tab-transaksi.nilaiPabean.title': 'Nilai Pabean dalam Rupiah',
  'tours.pib-tab-transaksi.nilaiPabean.body':
    'Nilai pabean hasil perhitungan, dalam rupiah. Inilah angka yang dipakai di tab Pungutan, jadi periksa dulu sebelum lanjut.',
  'tours.pib-tab-transaksi.berat.title': 'Berat Kotor dan Bersih (KGM)',
  'tours.pib-tab-transaksi.berat.body':
    'Keduanya dalam kilogram. <b>Netto tidak boleh nol dan tidak boleh melebihi bruto</b> — formulir ini menolak keduanya. Cocokkan dengan Packing List.',
  'tours.pib-tab-transaksi.komponenBiaya.title': 'Komponen Biaya',
  'tours.pib-tab-transaksi.komponenBiaya.body':
    'Rincian komponen pembentuk nilai pabean. Modal ini <b>harus dibuka dan disimpan minimal sekali</b> sebelum dokumen bisa dikirim.<br /><br />Klik untuk membukanya — panduan akan ditutup.',

  // ---- pib-tab-barang ---------------------------------------------------
  'tours.pib-tab-barang.intro.title': 'Tab Barang',
  'tours.pib-tab-barang.intro.body':
    'Setiap jenis barang didaftarkan sebagai satu baris, dan Bea Cukai menghitung pungutan per baris.',
  'tours.pib-tab-barang.baris.title': 'Daftar barang',
  'tours.pib-tab-barang.baris.body':
    'Jumlah barisnya harus sesuai dengan Invoice dan Packing List. Tiap baris memuat HS Code, uraian, jumlah, satuan, dan berat — HS Code menentukan tarif, jadi periksa yang itu lebih dulu. Tab ini juga punya saringan <b>Lartas</b> dan <b>Non-Lartas</b> untuk memisahkan barang yang tertandai.',
  'tours.pib-tab-barang.excel.title': 'Kalau barangnya banyak',
  'tours.pib-tab-barang.excel.body':
    'Untuk puluhan atau ratusan baris, unduh ke Excel, sunting di sana, lalu unggah kembali. Jauh lebih cepat daripada memperbaiki satu per satu di layar.',
  'tours.pib-tab-barang.next.title': 'Lanjut ke Pungutan',
  'tours.pib-tab-barang.next.body': 'Klik Selanjutnya untuk melihat perhitungan bea dan pajaknya.',

  // ---- pib-tab-pungutan -------------------------------------------------
  'tours.pib-tab-pungutan.intro.title': 'Tab Pungutan',
  'tours.pib-tab-pungutan.intro.body':
    'Rincian pungutan untuk dokumen ini. Angkanya dihitung dari data di tab Transaksi dan tab Barang — kalau ada yang janggal, telusuri ke sana.',
  'tours.pib-tab-pungutan.jenis.title': 'Jenis pungutan',
  'tours.pib-tab-pungutan.jenis.body':
    '<b>BM</b> bea masuk (dari HS Code), <b>PPN</b> pajak pertambahan nilai, <b>PPh 22</b> pajak penghasilan impor, dan <b>PPnBM</b> untuk barang mewah. Tidak semuanya selalu muncul — tergantung jenis barangnya.',
  'tours.pib-tab-pungutan.generate.title': 'Generate Pungutan',
  'tours.pib-tab-pungutan.generate.body':
    'Buka menu <b>Aksi</b> di kanan atas, lalu pilih <b>Generate Pungutan</b> untuk menghitung ulang seluruh pungutan. Kalau dokumen sudah punya nomor aju, perhitungan diambil dari CEISA; kalau belum, dihitung lokal dari data barang. Jalankan lagi setiap kali Anda mengubah nilai atau HS Code.',
  'tours.pib-tab-pungutan.next.title': 'Lanjut ke Pernyataan',
  'tours.pib-tab-pungutan.next.body': 'Klik Selanjutnya untuk sampai ke tab terakhir.',

  // ---- pib-tab-pernyataan -----------------------------------------------
  'tours.pib-tab-pernyataan.intro.title': 'Tab Pernyataan',
  'tours.pib-tab-pernyataan.intro.body':
    'Tab terakhir. Di sini Anda menyatakan bahwa data yang dilaporkan sudah benar, jadi periksa sekali lagi sebelum mengisinya.',
  'tours.pib-tab-pernyataan.tanggungJawab.title': 'Siapa yang bertanggung jawab',
  'tours.pib-tab-pernyataan.tanggungJawab.body':
    'Nama dan jabatan orang yang bertanggung jawab atas pemberitahuan ini. Kalau Anda tidak yakin siapa yang harus dicantumkan, tanyakan dulu ke atasan Anda.',
  'tours.pib-tab-pernyataan.cetak.title': 'Cetak dulu, baru kirim',
  'tours.pib-tab-pernyataan.cetak.body':
    'Sebelum mengirim, buka menu tindakan dan pilih <b>Cetak Formulir Draft CEISA</b>. Membaca hasil cetaknya adalah cara paling andal untuk menangkap kesalahan yang luput di layar.',
  'tours.pib-tab-pernyataan.kirim.title': 'Kirim ke CEISA',
  'tours.pib-tab-pernyataan.kirim.body':
    'Kalau semua sudah benar, pilih <b>Kirim</b> dari menu yang sama. Setelah terkirim, dokumen mendapat nomor pendaftaran dan perbaikan hanya bisa dilakukan lewat revisi.<br /><br />Selamat — Anda sudah menelusuri satu PIB dari awal sampai akhir.',
};

const zh: CopyDict = {
  // ---- Controls ---------------------------------------------------------
  'controls.next': '下一步',
  'controls.back': '上一步',
  'controls.done': '完成',
  'controls.progress': '第 {{current}} 步，共 {{total}} 步',
  'controls.clickToContinue': '请点击高亮元素以继续。',
  'controls.interactHint': '可以直接填写 — 填好后引导会自动继续。',
  'controls.exploreHint': '这个菜单仍然可以点击 — 尽管先看看，引导会在这里等您。',

  // ---- Help pill --------------------------------------------------------
  'pill.label': '指南',
  'pill.aria': '打开本页指南',
  'pill.pageGuide': '本页指南',
  'pill.tabGuide': '{{tab}} 标签页指南',
  'pill.menuGuide': 'Jarvis 菜单指南',
  'pill.section.split': '只看拆分单证的指南',
  'pill.onboarding': '新手引导',

  // ---- dashboard-welcome ------------------------------------------------
  'tours.dashboard-welcome.intro.title': '欢迎使用 Jarvis',
  'tours.dashboard-welcome.intro.body':
    'Jarvis 帮助您准备海关文件并提交至印尼海关系统 CEISA。这个简短的指南会告诉您从哪里开始。随时可以按 ✕ 或 Esc 退出。',
  'tours.dashboard-welcome.stats.title': '活动概览',
  'tours.dashboard-welcome.stats.body': '这里显示您创建的文件数量及其状态。首次登录时是空的，属于正常现象。',
  'tours.dashboard-welcome.nav.title': '左侧菜单',
  'tours.dashboard-welcome.nav.body':
    '所有工作都从这里开始。菜单按业务类型分组，而不是按单据类型 — 因此进口和出口在同一组内。',
  'tours.dashboard-welcome.search.title': '搜索菜单',
  'tours.dashboard-welcome.search.body': '忘记某个菜单在哪里时，直接输入名称即可，比逐个展开分组快得多。',
  'tours.dashboard-welcome.kepabeanan.title': '从「海关 (Kepabeanan)」开始',
  'tours.dashboard-welcome.kepabeanan.body':
    '这是您最常用的一组。里面有 <b>Buat Aju</b>（新建单据）、<b>Draft</b>（尚未提交的草稿）和 <b>Doc Pabean</b>（已提交的单据）。<br /><br />请点击展开。',
  'tours.dashboard-welcome.help.title': '指南随时都在这里',
  'tours.dashboard-welcome.help.body':
    '先说一件事：点击菜单底部的账户即可打开账户菜单，<b>Panduan</b>（指南）就在其中。随时点击即可重播当前页面的指南，或打开 Jarvis 全部菜单的完整说明。<br /><br />所以什么都不必记住 — 您随时可以回到这里。',
  'tours.dashboard-welcome.buatAju.title': '从这里开始：Buat Aju',
  'tours.dashboard-welcome.buatAju.body':
    '这是新建单据的入口。若要继续已有的草稿，请用它下方的 <b>Draft</b>。<br /><br />点击 <b>Buat Aju</b> 开始制作您的第一份 PIB — 下一段指南会在那里出现。',

  // ---- sidebar-map ------------------------------------------------------
  'tours.sidebar-map.intro.title': 'Jarvis 菜单地图',
  'tours.sidebar-map.intro.body': '本指南简要说明每一组菜单的用途。无需记忆 — 随时可以再次打开。',
  'tours.sidebar-map.mgmtDashboard.title': '管理看板 (Mgmt Dashboard)',
  'tours.sidebar-map.mgmtDashboard.body': '管理层报表：单据量、费用与各期趋势。通常由主管查看，而非日常操作人员。',
  'tours.sidebar-map.manifest.title': '舱单 (Manifest)',
  'tours.sidebar-map.manifest.body':
    '用于船舶舱单 (BC 1.1) — 一艘运输工具所载全部货物的清单。与 PIB 不同，PIB 只包含单一进口商的货物。',
  'tours.sidebar-map.kepabeanan.title': '海关 (Kepabeanan)',
  'tours.sidebar-map.kepabeanan.body':
    '主要分组。<b>Buat Aju</b> 新建单据，<b>Draft</b> 保存未提交的草稿，<b>Doc Pabean</b> 存放已提交单据，<b>Respon</b> 显示海关回复，<b>Monitoring Blokir</b> 监控被拦截的单据。',
  'tours.sidebar-map.kuota.title': '进出口配额 (Kuota Impor / Ekspor)',
  'tours.sidebar-map.kuota.body': '跟踪运输配额与参考价格。如果贵公司有进口量上限需要控制，这里会很有用。',
  'tours.sidebar-map.cukai.title': '消费税 (Cukai)',
  'tours.sidebar-map.cukai.body':
    '专用于应税消费品：送货单、CK 单据、物料清单与库存。仅当贵公司持有消费税许可时才相关。',
  'tours.sidebar-map.customerApproval.title': '客户审批 (Customer Approval)',
  'tours.sidebar-map.customerApproval.body':
    '如果单据在提交海关之前需要客户确认，流程在此配置 — 包括审核人是谁、请求发送到哪个邮件组。',
  'tours.sidebar-map.creditTopup.title': '额度充值 (Credit Topup)',
  'tours.sidebar-map.creditTopup.body': '提交单据会消耗额度。可在此充值并查看使用记录。',
  'tours.sidebar-map.langganan.title': '订阅 (Langganan)',
  'tours.sidebar-map.langganan.body': '贵公司的 Jarvis 订阅套餐及有效期。',
  'tours.sidebar-map.berita.title': '新闻 (Berita)',
  'tours.sidebar-map.berita.body': '来自多个来源的经济与贸易新闻摘要，无需离开系统即可浏览。',
  'tours.sidebar-map.layananLainnya.title': '其他服务 (Layanan Lainnya)',
  'tours.sidebar-map.layananLainnya.body': '实用小工具：文件压缩、PDF 拆分等。常用于上传前整理文件。',
  'tours.sidebar-map.master.title': '主数据 (Master)',
  'tours.sidebar-map.master.body': '主数据：用户、CEISA 账号、邮箱及其他设置。通常仅公司管理员可修改。',
  'tours.sidebar-map.preferences.title': '语言与外观',
  'tours.sidebar-map.preferences.body': '点击菜单底部的账户即可切换语言；主题在<b>设置</b>中更改。设置会保留到下次访问。',

  // ---- buat-aju ---------------------------------------------------------
  'tours.buat-aju.intro.title': '选择单据类型',
  'tours.buat-aju.intro.body':
    '每种海关单据都有自己的 BC 代码。此处的选择决定了后续使用的表单和校验规则，因此一开始就要选对。',
  'tours.buat-aju.export.title': '出口 — BC 3.0',
  'tours.buat-aju.export.body': 'PEB（出口货物申报单），用于离开印尼的货物。流程与 PIB 类似，但填报数据不同。',
  'tours.buat-aju.ppftz.title': 'PPFTZ',
  'tours.buat-aju.ppftz.body': '用于巴淡岛等自由贸易区的货物进出。仅当您所在地确实属于自贸区时才选择。',
  'tours.buat-aju.import.title': '进口 — BC 2.0 (PIB)',
  'tours.buat-aju.import.body':
    '这就是本指南要讲的：<b>PIB</b>，进口货物申报单。您就进入印尼的货物向海关提交的申报文件。<br /><br />点击此卡片开始。',

  // ---- doc-pabean-upload ------------------------------------------------
  'tours.doc-pabean-upload.intro.title': '从原始单证开始',
  'tours.doc-pabean-upload.intro.body':
    'Jarvis 不需要您重新录入所有内容。上传供应商的单证 — 发票、装箱单、提单 — Jarvis 会读取内容并为您填好 PIB 表单。',
  'tours.doc-pabean-upload.folderDropAll.title': '一次拖入全部文件',
  'tours.doc-pabean-upload.folderDropAll.body':
    '文件很多？全部拖到这里——或本页任意位置。JARVIS 会读取每个文件并放入对应文件夹。然后<b>由您</b>在右侧预览中逐一检查：点击<b>下一页</b>看完所有页面，再勾选<b>已检查</b>。',
  'tours.doc-pabean-upload.folderInvoice.title': '文件夹决定文件类型',
  'tours.doc-pabean-upload.folderInvoice.body':
    '放入<b>发票</b>文件夹的文件都按发票读取——包括含有“Ocean Freight”等费用的页面。内容看起来不同时，JARVIS 只会<b>提醒</b>，绝不会擅自更改类型。<br /><br />放错了？把文件拖到其他文件夹，或用该行的文件类型按钮更改。',
  'tours.doc-pabean-upload.folderChecklist.title': '完整性',
  'tours.doc-pabean-upload.folderChecklist.body':
    'PIB 需要 <b>BL / AWB</b> 和<b>发票</b>。这里显示还缺什么、哪些提示尚未处理。全部完成后按钮即可点击——下一步在审核页检查每份文件的类型和页面。',
  'tours.doc-pabean-upload.dropzone.gate': '请先上传至少一个文件才能继续。',
  'tours.doc-pabean-upload.dropzone.title': '上传文件',
  'tours.doc-pabean-upload.dropzone.body': '将 PDF 拖到此区域，或点击选择文件。可以一次上传多份单证。',
  'tours.doc-pabean-upload.docType.gate': '仍有文件未指定单证类型，请先全部填好。',
  'tours.doc-pabean-upload.docType.title': '标注每份单证的类型',
  'tours.doc-pabean-upload.docType.body':
    '每个文件都要标明类型：发票、装箱单、提单、原产地证等。Jarvis 会自动判断，但请<b>务必核对</b> — 类型错了，数据就会进错栏位。',
  'tours.doc-pabean-upload.autoSplit.title': '自动拆分 — 请勿跳过',
  'tours.doc-pabean-upload.autoSplit.body':
    '一个 PDF 文件常常包含<b>多份单证</b> — 例如发票和装箱单被扫描在一起。此按钮会读取 PDF 内容并按单证拆分。<br /><br />拆分正确很重要：Jarvis 会按单证类型分别读取，因此请确认这里的单证份数与原始文件的内容一致。<br /><br />接下来的几步会说明当自动结果不理想时如何手动拆分。',
  'tours.doc-pabean-upload.preview.title': '打开文件以预览',
  'tours.doc-pabean-upload.preview.body':
    '<b>点击列表中的任意一个 PDF 文件</b>，即可在右侧面板中打开。<br /><br />打开后，导览将继续介绍如何把一个文件拆分成多份单证。',
  'tours.doc-pabean-upload.reset.title': '传错了？',
  'tours.doc-pabean-upload.reset.body': '此按钮清空列表，让您从头开始。',
  'tours.doc-pabean-upload.process.title': '处理单证',
  'tours.doc-pabean-upload.process.body':
    '此按钮会随阶段变化：先是<b>处理</b>以读取单证，然后是<b>上传</b>，最后是<b>继续审阅</b>。较厚的单证可能需要一两分钟。<br /><br />点击继续。',

  // ---- doc-pabean-upload: splitting one PDF into several documents ------
  'tours.doc-pabean-upload.splitIntro.title': '把一个文件拆成多份单证',
  'tours.doc-pabean-upload.splitIntro.body':
    '此面板决定 Jarvis 从一个 PDF 中读出<b>几份单证</b>。一个分段 = 一份单证。<br /><br />若 <b>Pisah otomatis</b>（自动拆分）的结果已经正确，就无需改动。本指南用于结果不理想、或您想自己动手拆分的情况。',
  'tours.doc-pabean-upload.segmentAdd.gate': '点击「Tambah Segment」一次 — 之后指南会自动继续。',
  'tours.doc-pabean-upload.segmentAdd.title': '第 1 步 — 新建分段',
  'tours.doc-pabean-upload.segmentAdd.body':
    '点击 <b>Tambah Segment</b> <b>一次</b>，建立第一个分段。分段一出现指南就会自动继续 — 现在还不需要再加。<br /><br />新建的分段会成为<b>当前分段</b>：接下来选择的页面都会归入其中。等这个分段填好后，可以再为同一文件中的下一份单证新建分段。',
  'tours.doc-pabean-upload.segmentPages.gate': '仍有空的分段。请为它选择页面，或用该行右侧的垃圾桶图标删除它。',
  'tours.doc-pabean-upload.segmentPages.title': '第 2 步 — 选择页面',
  'tours.doc-pabean-upload.segmentPages.body':
    '点击页面缩略图把它归入当前分段，再次点击可移出。每一页只能属于一个分段。<br /><br />如果这个文件包含多份单证，重复同样的步骤：再次 <b>Tambah Segment</b>，然后为它选择页面 — 例如第 1–2 页是发票，第 3 页是装箱单。<br /><br />建立后却留空的分段会卡住流程；用不到就直接删除。',
  'tours.doc-pabean-upload.segmentRow.title': '第 3 步 — 核对分段内容',
  'tours.doc-pabean-upload.segmentRow.body':
    '每一行显示该分段已包含的页面。再次选页之前，请先点击某一行把它设为当前分段。<br /><br />出现 <i>"Setiap segmen harus memiliki minimal 1 halaman"</i>（每个分段至少要有一页）表示还有空分段 — 请补页或删除，该提示存在期间无法提取。',
  'tours.doc-pabean-upload.segmentType.title': '第 4 步 — 指定单证类型',
  'tours.doc-pabean-upload.segmentType.body':
    '为每个分段选择 <b>Jenis dokumen</b>（单证类型）：发票、装箱单、提单、原产地证等。<br /><br />它决定内容将被读入 PIB 的哪些栏位。类型不对，数据就会进错地方。',
  'tours.doc-pabean-upload.segmentOriginal.title': '第 5 步 — 原始文件如何处理',
  'tours.doc-pabean-upload.segmentOriginal.body':
    '勾选 <b>Hapus file asli</b>（删除原始文件）后，合并的原文件会被拆分结果取代。通常保持勾选 — 否则原文件也会一并处理，单证就会重复计算。',
  'tours.doc-pabean-upload.segmentExtract.title': '第 6 步 — 执行提取',
  'tours.doc-pabean-upload.segmentExtract.body':
    '此按钮按您设定的分段拆分文件。完成后每个分段都会作为独立文件出现在左侧列表中，您只需确认其单证类型无误。',

  // ---- doc-pabean-upload-export (PEB) -----------------------------------
  'tours.doc-pabean-upload-export.intro.title': '上传出口单证',
  'tours.doc-pabean-upload-export.intro.body':
    '流程与进口相同：上传您持有的单证，Jarvis 读取内容，结果填入 <b>PEB</b>（BC 3.0）表单。<br /><br />区别在于单证类型和之后的表单内容 — 这是货物<b>离境</b>的申报。',
  'tours.doc-pabean-upload-export.dropzone.title': '上传文件',
  'tours.doc-pabean-upload-export.dropzone.body': '将 PDF 拖到此区域，或点击选择。出口通常是发票、装箱单和运输单证。',
  'tours.doc-pabean-upload-export.dropzone.gate': '请先上传至少一个文件才能继续。',
  'tours.doc-pabean-upload-export.docType.title': '标注每份单证的类型',
  'tours.doc-pabean-upload-export.docType.body':
    '每个文件都要标明类型。Jarvis 会自动判断 — 请核对，因为类型错了内容就会进错栏位。货代的装运指示请选 000 - Shipping Instruction，而不是 AWB。',
  'tours.doc-pabean-upload-export.docType.gate': '仍有文件未指定单证类型，请先全部填好。',
  'tours.doc-pabean-upload-export.autoSplit.title': '自动拆分',
  'tours.doc-pabean-upload-export.autoSplit.body':
    '若一个 PDF 含多份单证，此按钮会读取并按单证拆分。请确认拆分后的份数与原始文件内容一致。',
  'tours.doc-pabean-upload-export.reset.title': '传错了？',
  'tours.doc-pabean-upload-export.reset.body': '此按钮清空列表，让您从头开始。',
  'tours.doc-pabean-upload-export.process.title': '处理单证',
  'tours.doc-pabean-upload-export.process.body':
    '此按钮随阶段变化：先<b>处理</b>，然后<b>上传</b>，之后进入 PEB 表单。<br /><br />点击继续。',

  // ---- export-form (PEB) -------------------------------------------------
  'tours.export-form.intro.title': 'PEB 表单 (BC 3.0)',
  'tours.export-form.intro.body':
    '这是<b>出口货物申报单</b> — 用于离开印尼的货物。Jarvis 已根据您上传的单证填好部分内容；请核对结果、修正有误之处、补齐空白。<br /><br />本指南介绍每个标签页以及提交按钮。',
  'tours.export-form.actions.title': '「Aksi」操作菜单',
  'tours.export-form.actions.body':
    '这里提供打印表单、修订等其他操作。提交前通读打印件，是发现屏幕上漏看错误的好办法。',
  'tours.export-form.submit.title': '申报号与提交',
  'tours.export-form.submit.body':
    'PEB 的申报号（Nomor Aju）显示在这里，可通过旁边的图标修改或重新获取。「Submit Draft」按钮把 PEB 提交给 CEISA。按下之前请确认所有标签页均无错误 — 提交后，更正需通过「Aksi」菜单中的修订进行。',

  // ---- PEB form tabs -----------------------------------------------------
  'tours.export-form.tabs.title': '八个标签页',
  'tours.export-form.tabs.body':
    'PEB 数据分为八个标签页。标为<b>红色</b>的表示其中仍有错误或缺失。<br /><br />这里没有像 PIB 那样独立的「税费」标签页 — 出口的 <b>Bea Keluar</b>（出口关税）、<b>PPh 22</b> 和 <b>Pungutan Sawit</b>（棕榈油征费）都在「Transaksi」标签页内。<br /><br />每个标签页都有自己的指南：首次打开某个标签页时会自动出现。',

  // ---- PEB form tabs -----------------------------------------------------
  'tours.peb-tab-header.intro.title': 'Header 标签页',
  'tours.peb-tab-header.intro.body': '出口单据的基本身份信息：货物从何处装运、由哪个海关办事处受理、属于哪类出口。',
  'tours.peb-tab-header.pelabuhan.title': '出口装货港 (Pelabuhan Muat Ekspor)',
  'tours.peb-tab-header.pelabuhan.body': '货物出口装运的港口。请与运输单证核对。',
  'tours.peb-tab-header.kantor.title': '起运地海关 (Kantor Pabean Muat Asal)',
  'tours.peb-tab-header.kantor.body': '货物开始装运地点的海关办事处。会根据所选港口自动带出部分内容 — 请核对结果。',
  'tours.peb-tab-header.kantorEkspor.title': '出口地海关 (Kantor Pabean Muat Ekspor)',
  'tours.peb-tab-header.kantorEkspor.body': '货物实际出口地点的海关办事处。若货物未先行转运，则与起运地海关相同。',
  'tours.peb-tab-header.jenisEkspor.title': '出口类型 (Jenis Ekspor)',
  'tours.peb-tab-header.jenisEkspor.body':
    '本次发运的出口方案。默认为 <b>1 - EKSPOR BIASA</b>（普通出口）；其他选项用于特定方案。',
  'tours.peb-tab-header.kategori.title': '出口类别 (Kategori Ekspor)',
  'tours.peb-tab-header.kategori.body':
    '发运的类别，例如 <b>10 - UMUM</b>（一般）。请按贵公司的方案选择；不确定时请咨询报关团队。',
  'tours.peb-tab-header.caraDagang.title': '贸易方式 (Cara Dagang)',
  'tours.peb-tab-header.caraDagang.body':
    '与买方约定的贸易条件，例如 <b>1 - BIASA</b>（普通）。请与合同或发票保持一致。',
  'tours.peb-tab-header.caraBayar.title': '付款方式 (Cara Bayar)',
  'tours.peb-tab-header.caraBayar.body':
    '本次交易的付款方式，例如 <b>1 - BIASA/TUNAI</b>。旁边的<b>付款说明</b>可填写补充备注。',
  'tours.peb-tab-header.komoditi.title': '商品类别 (Komoditi)',
  'tours.peb-tab-header.komoditi.body': '可选 <b>1 - MIGAS</b>（油气）或 <b>2 - NON MIGAS</b>（非油气）。',
  'tours.peb-tab-header.curah.title': '散装 (Curah)',
  'tours.peb-tab-header.curah.body': '标明货物是否以散装形式（无单件包装）发运。',
  'tours.peb-tab-header.barangKiriman.title': '寄递货物出口 (Ekspor Barang Kiriman)',
  'tours.peb-tab-header.barangKiriman.body':
    '标明是否通过邮政或快递发运。默认为 <b>T - BUKAN EKSPOR BARANG KIRIMAN</b>。',
  'tours.peb-tab-header.next.title': '进入 Entitas',
  'tours.peb-tab-header.next.body': '点击 <b>Selanjutnya</b>（下一步）。下一个标签页的指南会在首次打开时出现。',

  'tours.peb-tab-entitas.intro.title': 'Entitas 标签页',
  'tours.peb-tab-entitas.intro.body': '本次出口涉及的各方，从<b>出口商税号</b>开始。请注意收货人与买方是分开的。',
  'tours.peb-tab-entitas.eksportir.title': '出口商名称',
  'tours.peb-tab-entitas.eksportir.body': '出口企业的名称。请与公司正式文件核对。',
  'tours.peb-tab-entitas.alamatEksportir.title': '出口商地址',
  'tours.peb-tab-entitas.alamatEksportir.body': '出口商的登记地址，以正式文件为准。',
  'tours.peb-tab-entitas.penerima.title': '收货人名称',
  'tours.peb-tab-entitas.penerima.body': '境外<b>收取</b>货物的一方，以运输单证为准。',
  'tours.peb-tab-entitas.negaraPenerima.title': '收货人国家',
  'tours.peb-tab-entitas.negaraPenerima.body': '收货人所在国家，可能与买方国家不同。',
  'tours.peb-tab-entitas.pembeli.title': '买方名称',
  'tours.peb-tab-entitas.pembeli.body': '<b>购买</b>货物的一方 — 常与收货人不同。请按发票填写。',
  'tours.peb-tab-entitas.negaraPembeli.title': '买方国家',
  'tours.peb-tab-entitas.negaraPembeli.body': '买方所在国家。与收货人国家不同时，两者仍分别申报。',
  'tours.peb-tab-entitas.ppjk.title': '报关代理 (PPJK)',
  'tours.peb-tab-entitas.ppjk.body': '委托报关代理办理时填写。附近还有<b>PPJK 识别号</b>与<b>PPJK 地址</b>。',
  'tours.peb-tab-entitas.konsolidasi.title': '拼箱地点',
  'tours.peb-tab-entitas.konsolidasi.body': '货物在装运前与其他发运合并时填写。',
  'tours.peb-tab-entitas.next.title': '进入 Document',
  'tours.peb-tab-entitas.next.body': '点击 <b>Selanjutnya</b>（下一步）进入下一个标签页。',

  'tours.peb-tab-document.intro.title': 'Document 标签页',
  'tours.peb-tab-document.intro.body': '出口随附单证及其编号和日期。',
  'tours.peb-tab-document.daftar.title': '单证清单',
  'tours.peb-tab-document.daftar.body':
    '在此新增、修改、复制或删除行。请对照原件核对编号与日期。单证也可通过<b>Lampirkan Dokumen ke Barang</b> 关联到特定货物。',
  'tours.peb-tab-document.next.title': '进入 Pengangkut',
  'tours.peb-tab-document.next.body': '点击 <b>Selanjutnya</b>（下一步）进入下一个标签页。',

  'tours.peb-tab-pengangkut.intro.title': '起运装货港',
  'tours.peb-tab-pengangkut.intro.body': 'Pengangkut 标签页说明货物出境的行程，从最初装货的港口开始。',
  'tours.peb-tab-pengangkut.muatEkspor.title': '出口装货港',
  'tours.peb-tab-pengangkut.muatEkspor.body': '货物实际出口的港口。若未先行转运，则与起运装货港相同。',
  'tours.peb-tab-pengangkut.penimbunan.title': '堆存地点',
  'tours.peb-tab-pengangkut.penimbunan.body': '装运前货物堆存的地点。',
  'tours.peb-tab-pengangkut.tanggal.title': '预计出口日期',
  'tours.peb-tab-pengangkut.tanggal.body': '按运输安排预计的出口日期。',
  'tours.peb-tab-pengangkut.sarana.title': '运输方式',
  'tours.peb-tab-pengangkut.sarana.body': '承运货物出境的运输方式。请从运输单证照抄。',
  'tours.peb-tab-pengangkut.tujuan.title': '目的港',
  'tours.peb-tab-pengangkut.tujuan.body': '货物的最终目的港。',
  'tours.peb-tab-pengangkut.negara.title': '出口目的国',
  'tours.peb-tab-pengangkut.negara.body': '货物的目的国。请与发票及运输单证核对。',
  'tours.peb-tab-pengangkut.pemeriksaan.title': '查验',
  'tours.peb-tab-pengangkut.pemeriksaan.body':
    '<b>查验地点</b>，附近还有<b>查验海关办事处</b>与<b>查验日期</b> — 货物在装运前安排查验时填写。',
  'tours.peb-tab-pengangkut.next.title': '进入 Kemasan',
  'tours.peb-tab-pengangkut.next.body': '点击 <b>Selanjutnya</b>（下一步）进入下一个标签页。',

  'tours.peb-tab-kemasan.intro.title': 'Kemasan & Peti Kemas 标签页',
  'tours.peb-tab-kemasan.intro.body': '发运了多少件包装，以及货物装在哪个集装箱里。',
  'tours.peb-tab-kemasan.kemasan.title': '包装清单',
  'tours.peb-tab-kemasan.kemasan.body': '在此新增与修改行。请与装箱单，以及在「Transaksi」填写的重量核对。',
  'tours.peb-tab-kemasan.next.title': '进入 Transaksi',
  'tours.peb-tab-kemasan.next.body': '点击 <b>Selanjutnya</b>（下一步）进入下一个标签页。',

  'tours.peb-tab-transaksi.intro.title': '币种 (Valuta)',
  'tours.peb-tab-transaksi.intro.body':
    'Transaksi 标签页是本次出口的财务面，也是计算出口征费的地方。先从交易币种开始。',
  'tours.peb-tab-transaksi.ndpbm.title': 'NDPBM',
  'tours.peb-tab-transaksi.ndpbm.body': '用于折算上方币种的基准值。不确定适用哪个数值时，请咨询贵公司的报关团队。',
  'tours.peb-tab-transaksi.penyerahan.title': '交货方式',
  'tours.peb-tab-transaksi.penyerahan.body': '与买方约定的交货条件。请与合同或发票保持一致。',
  'tours.peb-tab-transaksi.nilai.title': '出口价值 (Incoterm FOB)',
  'tours.peb-tab-transaksi.nilai.body': '出口货物的价值，按栏位标签所示以 <b>FOB</b> 为基础填写。',
  'tours.peb-tab-transaksi.asuransi.title': '保险费',
  'tours.peb-tab-transaksi.asuransi.body': '本次发运的保险金额及保险类型。',
  'tours.peb-tab-transaksi.maklon.title': '加工费 (Nilai Maklon)',
  'tours.peb-tab-transaksi.maklon.body': '用于来料加工 — 由他方所有、经您加工后再出口的货物。',
  'tours.peb-tab-transaksi.beratKotor.title': '毛重 (KGM)',
  'tours.peb-tab-transaksi.beratKotor.body': '含包装的重量，单位千克。请与装箱单核对。',
  'tours.peb-tab-transaksi.beratBersih.title': '净重 (KGM)',
  'tours.peb-tab-transaksi.beratBersih.body':
    '仅货物本身的重量，单位千克。本标签页另有 <b>Info Bea Keluar</b>、<b>Info PPh 22</b>、<b>Info Pungutan Sawit</b> 面板与 <b>Preview Bea Keluar</b> — 金额或货物变动后请重新运行。',
  'tours.peb-tab-transaksi.next.title': '进入 Barang',
  'tours.peb-tab-transaksi.next.body': '点击 <b>Selanjutnya</b>（下一步）进入下一个标签页。',

  'tours.peb-tab-barang.intro.title': 'Barang 标签页',
  'tours.peb-tab-barang.intro.body': '每种出口货物登记为一行。',
  'tours.peb-tab-barang.baris.title': '货物清单',
  'tours.peb-tab-barang.baris.body':
    '行数应与发票和装箱单相符。每行包含 HS 编码、数量、单位与重量；搜索框可按品名或 HS 编码查找。',
  'tours.peb-tab-barang.next.title': '进入 Pernyataan',
  'tours.peb-tab-barang.next.body': '点击 <b>Selanjutnya</b>（下一步）进入最后一个标签页。',

  'tours.peb-tab-pernyataan.intro.title': '姓名 (Nama)',
  'tours.peb-tab-pernyataan.intro.body': '最后一个标签页。对本次出口申报负责的人员姓名。',
  'tours.peb-tab-pernyataan.jabatan.title': '职务 (Jabatan)',
  'tours.peb-tab-pernyataan.jabatan.body': '该人员在公司中的职务。',
  'tours.peb-tab-pernyataan.tempat.title': '地点 (Tempat)',
  'tours.peb-tab-pernyataan.tempat.body': '本申报的填制地点。',
  'tours.peb-tab-pernyataan.tanggal.title': '日期 (Tanggal)',
  'tours.peb-tab-pernyataan.tanggal.body': '本申报的填制日期。',
  'tours.peb-tab-pernyataan.submit.title': '申报号与提交',
  'tours.peb-tab-pernyataan.submit.body':
    'PEB 的申报号（Nomor Aju）显示在这里，可通过旁边的图标修改或重新获取。「Submit Draft」按钮把 PEB 提交给 CEISA。按下之前请确认所有标签页均无错误 — 提交后，更正需通过「Aksi」菜单中的修订进行。',

  // ---- doc-pabean-draft-list --------------------------------------------
  'tours.doc-pabean-draft-list.intro.title': '草稿列表',
  'tours.doc-pabean-draft-list.intro.body':
    '草稿是<b>尚未提交</b>给海关的单据 — 任何内容都还可以修改。勾选多行可一次删除。',
  'tours.doc-pabean-draft-list.search.title': '搜索草稿',
  'tours.doc-pabean-draft-list.search.body': '可按申报号、进口商名称或发票号搜索。草稿变多之后会非常有用。',
  'tours.doc-pabean-draft-list.open.title': '打开草稿',
  'tours.doc-pabean-draft-list.open.body':
    '点击任意一行打开其 PIB 表单。您将在那里核对 Jarvis 的读取结果并补全缺失内容。<br /><br />点击一份草稿以继续。',

  // ---- manifest-from-pabean ---------------------------------------------
  'tours.manifest-from-pabean.note.title': '仅限 JARVIS 中的 PEB',
  'tours.manifest-from-pabean.note.body':
    '此列表只包含通过 JARVIS 创建且已获 <b>NPE</b> 回执的 PEB。不是通过 JARVIS 创建的 PEB 可以稍后在草稿中通过 <b>Upload PEB Susulan</b> 添加。',
  'tours.manifest-from-pabean.table.title': '勾选一起装运的 PEB',
  'tours.manifest-from-pabean.table.body':
    '每个 PEB 生成一个分提单/分运单。<b>勾选顺序</b>即舱单上的 pos 顺序，舱单表头以第一个 PEB 为准。标有“已在草稿中”的 PEB 仍可选择。',
  'tours.manifest-from-pabean.create.title': '创建舱单',
  'tours.manifest-from-pabean.create.body':
    '草稿根据 PEB 数据生成——无需上传，无需 AI 提取——然后在编辑器中打开。主提单可以稍后填写。',

  // ---- manifest-from-pabean-inward --------------------------------------
  'tours.manifest-from-pabean-inward.note.title': '仅限 JARVIS 中的 PIB',
  'tours.manifest-from-pabean-inward.note.body':
    '此列表只包含通过 JARVIS 创建且已获 <b>SPPB</b> 回执的 PIB。不是通过 JARVIS 创建的 PIB，其分提单/分运单可以稍后在草稿中通过 <b>Upload BL/AWB Susulan</b> 添加。',
  'tours.manifest-from-pabean-inward.table.title': '勾选一起拼箱的 PIB',
  'tours.manifest-from-pabean-inward.table.body':
    '每个 PIB 生成一个分提单/分运单。<b>勾选顺序</b>即舱单上的 pos 顺序，舱单表头以第一个 PIB 为准。BC 1.1 列显示 PIB 引用的承运人原始舱单。',
  'tours.manifest-from-pabean-inward.create.title': '创建舱单',
  'tours.manifest-from-pabean-inward.create.body':
    '入境草稿根据 PIB 数据生成——无需上传，无需 AI 提取——然后在编辑器中打开。主提单/主运单可以稍后填写。',

  // ---- doc-pabean-list --------------------------------------------------
  'tours.doc-pabean-list.intro.title': '已提交的单据',
  'tours.doc-pabean-list.intro.body': '与草稿不同，本页存放<b>已经</b>提交至 CEISA 的单据。您在这里跟踪它们的进展。',
  'tours.doc-pabean-list.credits.title': '额度余额',
  'tours.doc-pabean-list.credits.body':
    '提交单据会消耗额度。可在此查看余额，并在余额不足前通过「Topup Kredit」（额度充值）充值。',
  'tours.doc-pabean-list.filters.title': '筛选与排序',
  'tours.doc-pabean-list.filters.body':
    '可按状态、日期或分公司筛选。如果贵公司有多家分公司，分公司筛选可以只显示属于您的单据。',
  'tours.doc-pabean-list.status.title': '读懂状态列',
  'tours.doc-pabean-list.status.body':
    '状态显示单据在海关的进展。请特别留意<b>被拒绝</b>或<b>被拦截</b>的单据 — 两者都需要跟进，单据的「Aksi」（操作）菜单中提供 <b>Revisi</b>（修订）选项用于重新提交更正。',
  'tours.doc-pabean-list.report.title': '下载报表',
  'tours.doc-pabean-list.report.body': '生成指定日期范围内的单据汇总，用于内部报告或审计。',

  // ---- pib-edit-overview ------------------------------------------------
  'tours.pib-edit-overview.intro.title': 'PIB 表单',
  'tours.pib-edit-overview.intro.body':
    '这是工作的核心。Jarvis 已根据您上传的单证填好了部分栏位 — <b>请核对结果、修正有误之处、补齐仍为空的栏位</b>。<br /><br />原始单证显示在左侧，方便直接比对。',
  'tours.pib-edit-overview.errors.title': '错误清单',
  'tours.pib-edit-overview.errors.body':
    '红色面板列出所有阻碍提交的问题。点击某条错误可直接跳到对应栏位。<br /><br />另有 <b>Bot Assist</b> 逐条引导修正 — 错误较多时非常有用。',
  'tours.pib-edit-overview.toolbar.title': '辅助工具',
  'tours.pib-edit-overview.toolbar.body':
    '把数据导出到 Excel、在那里修改、再传回来 — 货物有几十上百行时这是最快的方式。还可以与历史数据同步，沿用以往同类单据的填法。',
  'tours.pib-edit-overview.actions.title': '校验、打印、提交',
  'tours.pib-edit-overview.actions.body':
    '正确顺序是：先<b>校验 (Validasi)</b> 确认 CEISA 能接受，再<b>打印 CEISA 草稿表 (Cetak Formulir Draft)</b> 供人工复核，最后才<b>提交 (Kirim)</b>。<br /><br />提交之后只能通过修订更正，所以不要跳过打印这一步。',
  'tours.pib-edit-overview.footer.title': '在标签页之间切换',
  'tours.pib-edit-overview.footer.body':
    '「上一步」和「下一步」按顺序遍历各标签页。切换标签页会自动保存您的修改 — 没有单独的保存按钮。',
  'tours.pib-edit-overview.tabs.title': '九个标签页',
  'tours.pib-edit-overview.tabs.body':
    'PIB 数据分为九个标签页。显示<b>红色</b>的标签页表示其中仍有错误或缺失。<br /><br />每个标签页都有自己的指南：首次打开某个标签页时，对应指南会自动出现。',

  // ---- pib-tab-header ---------------------------------------------------
  'tours.pib-tab-header.intro.title': 'Header 标签页',
  'tours.pib-tab-header.intro.body':
    '单据的基本身份信息：货物从哪个港口进入、由哪个海关办事处受理、用途是什么。这里的填写会影响后续标签页适用的规则。',
  'tours.pib-tab-header.pelabuhan.title': '港口 (Pelabuhan)',
  'tours.pib-tab-header.pelabuhan.body': '货物卸货的港口。请与提单上记载的港口保持一致。',
  'tours.pib-tab-header.kantorPabean.title': '海关办事处 (Kantor Pabean)',
  'tours.pib-tab-header.kantorPabean.body':
    '负责受理该批货物的海关办事处。此栏会随港口自动联动 — 但如果您更换了港口，请<b>再次核对办事处</b>。',
  'tours.pib-tab-header.jenisPib.title': 'PIB 类型',
  'tours.pib-tab-header.jenisPib.body':
    '可选 <b>1 - BIASA</b>（普通）或 <b>2 - BERKALA</b>（定期）。定期用于定期申报方案；若不确定贵公司是否采用，请先咨询主管或报关团队。',
  'tours.pib-tab-header.jenisImpor.title': '进口类型 (Jenis Impor)',
  'tours.pib-tab-header.jenisImpor.body':
    '货物进境的目的：<b>1 - UNTUK DIPAKAI</b>（供使用）、<b>2 - SEMENTARA</b>（暂时进口）、<b>5 - RUSH HANDLING</b> 或 <b>9 - GABUNGAN 1 DAN 2</b>（1 与 2 的合并）。<br /><br />请按实际进口用途选择。不确定时，可比对同类货物以往的进口单据。',
  'tours.pib-tab-header.caraBayar.title': '付款方式 (Cara Bayar)',
  'tours.pib-tab-header.caraBayar.body':
    '本次交易适用的付款方式。最常用的是 <b>1 - BIASA/TUNAI</b>（普通/现金）与 <b>2 - BERKALA</b>（定期）；其余选项对应向供应商付款的条件 — 赊账 (Open Account)、电汇 (T/T)、预付 (Advance Payment)、即期信用证、托收，或无需付款。<br /><br />请与发票或合同上的付款条件保持一致。',
  'tours.pib-tab-header.next.title': '进入 Entitas',
  'tours.pib-tab-header.next.body': '点击「下一步」。您的修改会自动保存，下一个标签页的指南也会随之出现。',

  // ---- pib-tab-entitas --------------------------------------------------
  'tours.pib-tab-entitas.intro.title': 'Entitas 标签页',
  'tours.pib-tab-entitas.intro.body':
    '本次进口涉及的各方，各有独立区块：进口商、货主、发货人、卖方、报关代理与集中地。每一部分分别申报。',
  'tours.pib-tab-entitas.importir.title': '进口商 (Importir)',
  'tours.pib-tab-entitas.importir.body': '登记为进口方的企业，含税号与地址。请与该公司的正式文件核对。',
  'tours.pib-tab-entitas.pemilik.title': '货主 (Pemilik Barang)',
  'tours.pib-tab-entitas.pemilik.body': '真正拥有货物的一方。可能与进口商相同，也可能不同 — 例如以他人名义办理进口时。',
  'tours.pib-tab-entitas.pengirim.title': '发货人 (Pengirim)',
  'tours.pib-tab-entitas.pengirim.body': '自境外发运货物的一方，以运输单证为准。',
  'tours.pib-tab-entitas.penjual.title': '卖方 (Penjual)',
  'tours.pib-tab-entitas.penjual.body': '出售货物的一方。常与发货人相同，但并不总是如此 — 请按发票填写。',
  'tours.pib-tab-entitas.pemusatan.title': '集中地 (Pemusatan)',
  'tours.pib-tab-entitas.pemusatan.body': '贵公司采用集中申报方案时填写。否则请留空。',
  'tours.pib-tab-entitas.ppjk.title': '报关代理 (PPJK)',
  'tours.pib-tab-entitas.ppjk.body': '委托报关代理办理时填写此区块。若由公司自行办理，可以留空。',
  'tours.pib-tab-entitas.next.title': '进入 Dokumen',
  'tours.pib-tab-entitas.next.body': '点击「下一步」进入单证标签页。',

  // ---- pib-tab-dokumen --------------------------------------------------
  'tours.pib-tab-dokumen.intro.title': 'Dokumen 标签页',
  'tours.pib-tab-dokumen.intro.body': '随附单证及其编号和日期的清单。本标签页还提供 HS 编码筛选与批量填写许可编号。',
  'tours.pib-tab-dokumen.wajib.title': '单证清单',
  'tours.pib-tab-dokumen.wajib.body':
    '请逐行对照原件核对编号与日期。原始单证上的日期有时写成两位数年份 — 请确认这里保存的年份正确。',
  'tours.pib-tab-dokumen.tambah.title': '新增单证',
  'tours.pib-tab-dokumen.tambah.body':
    '此按钮打开表单，用于补充未被自动识别的单证。<br /><br />点击打开 — 指南会自动关闭以免遮挡。',

  // ---- pib-tab-pengangkut -----------------------------------------------
  'tours.pib-tab-pengangkut.intro.title': 'Pengangkut 标签页',
  'tours.pib-tab-pengangkut.intro.body': '承运货物的运输工具信息。海关据此把您的 PIB 与他们先前收到的船舶舱单相匹配。',
  'tours.pib-tab-pengangkut.sarana.title': '运输工具与航次',
  'tours.pib-tab-pengangkut.sarana.body': '船名或航班号，以及航次号。请按提单上的写法原样照抄，包括拼写。',
  'tours.pib-tab-pengangkut.bc11.title': 'BC 1.1 编号',
  'tours.pib-tab-pengangkut.bc11.body':
    'BC 1.1 是承运人向海关申报的舱单，包含全船货物。此编号把您的货物与该舱单关联起来。',
  'tours.pib-tab-pengangkut.pos.title': '提单项号与分项号 (Pos / Sub Pos)',
  'tours.pib-tab-pengangkut.pos.body':
    '您的货物在 BC 1.1 舱单中的序号。若手头没有，请向承运本次发运的船公司或货代索取。',
  'tours.pib-tab-pengangkut.tanggalTutup.title': '结关日期 (Tanggal Tutup PU)',
  'tours.pib-tab-pengangkut.tanggalTutup.body': '该运输工具的舱单结关日期，以 BC 1.1 数据为准。',
  'tours.pib-tab-pengangkut.subpos.title': '子序号 (Nomor Subpos)',
  'tours.pib-tab-pengangkut.subpos.body': '序号之下的细分。若您收到的舱单载有该项，请填写。',
  'tours.pib-tab-pengangkut.subsubpos.title': '子子序号 (Nomor Subsubpos)',
  'tours.pib-tab-pengangkut.subsubpos.body': '子序号之下最细的一级，常为空白 — 仅在确有记载时填写。',
  'tours.pib-tab-pengangkut.pelTujuan.title': '目的港 (Pelabuhan Tujuan)',
  'tours.pib-tab-pengangkut.pelTujuan.body':
    '货物的目的港。表单中还有<b>装货港</b>与运输工具<b>船旗</b> — 三者都请与运输单证核对。',
  'tours.pib-tab-pengangkut.next.title': '进入 Kemasan',
  'tours.pib-tab-pengangkut.next.body': '点击「下一步」进入包装与集装箱标签页。',

  // ---- pib-tab-kemasan --------------------------------------------------
  'tours.pib-tab-kemasan.intro.title': '包装 (Kemasan)',
  'tours.pib-tab-kemasan.intro.body': '本标签页有两个清单：上方的<b>包装</b>与下方的<b>集装箱</b>。',
  'tours.pib-tab-kemasan.jumlah.title': '包装清单',
  'tours.pib-tab-kemasan.jumlah.body':
    '包装件数与种类，例如「120 CARTON」。请与装箱单，以及在「Transaksi」填写的重量核对。',
  'tours.pib-tab-kemasan.petiKemas.title': '集装箱 (Peti Kemas)',
  'tours.pib-tab-kemasan.petiKemas.body': '箱号及其尺寸与类型，以提单为准。',
  'tours.pib-tab-kemasan.next.title': '进入 Transaksi',
  'tours.pib-tab-kemasan.next.body': '点击「下一步」进入交易标签页。',

  // ---- pib-tab-transaksi ------------------------------------------------
  'tours.pib-tab-transaksi.intro.title': '币种 (Valuta)',
  'tours.pib-tab-transaksi.intro.body': 'Transaksi 标签页是本次进口的财务面。先从发票所用的币种开始。',
  'tours.pib-tab-transaksi.ndpbm.title': 'NDPBM',
  'tours.pib-tab-transaksi.ndpbm.body': '用于折算上方币种的基准值。不确定适用哪个数值时，请咨询贵公司的报关团队。',
  'tours.pib-tab-transaksi.jenisTransaksi.title': '交易类型 (Jenis Transaksi)',
  'tours.pib-tab-transaksi.jenisTransaksi.body': '本次采购交易的性质。请与合同或发票保持一致。',
  'tours.pib-tab-transaksi.incoterm.title': '贸易术语 (Incoterm)',
  'tours.pib-tab-transaksi.incoterm.body':
    '与供应商约定的交货条件 — FOB、CIF、CFR 等。它决定哪些费用已包含在发票价格中。',
  'tours.pib-tab-transaksi.hargaBarang.title': '货物价值 (Harga Barang)',
  'tours.pib-tab-transaksi.hargaBarang.body': '发票所载货物价值，以上方所选币种计。',
  'tours.pib-tab-transaksi.asuransi.title': '保险费 (Asuransi)',
  'tours.pib-tab-transaksi.asuransi.body': '本次发运的保险金额。是否需要填写取决于所选贸易术语 — 请与发票和保单核对。',
  'tours.pib-tab-transaksi.nilaiPabean.title': '海关价值（卢比）',
  'tours.pib-tab-transaksi.nilaiPabean.body':
    '计算得出的完税价格，以卢比计。这就是「税费」标签页所用的数值，继续前请先核对。',
  'tours.pib-tab-transaksi.berat.title': '毛重与净重 (KGM)',
  'tours.pib-tab-transaksi.berat.body':
    '两者均以千克计。<b>净重不得为零，也不得大于毛重</b> — 本表单对这两种情况都会拒绝。请与装箱单核对。',
  'tours.pib-tab-transaksi.komponenBiaya.title': '费用构成 (Komponen Biaya)',
  'tours.pib-tab-transaksi.komponenBiaya.body':
    '完税价格构成项的明细。提交单据前<b>必须至少打开并保存一次</b>此弹窗。<br /><br />点击打开 — 指南会自动关闭。',

  // ---- pib-tab-barang ---------------------------------------------------
  'tours.pib-tab-barang.intro.title': 'Barang 标签页',
  'tours.pib-tab-barang.intro.body': '每种货物登记为一行，海关逐行计算税费。',
  'tours.pib-tab-barang.baris.title': '货物清单',
  'tours.pib-tab-barang.baris.body':
    '行数应与发票和装箱单相符。每行包含 HS 编码、品名、数量、单位与重量 — HS 编码决定税率，请优先核对。本标签页还提供 <b>Lartas</b> 与 <b>Non-Lartas</b> 筛选，用于分开查看被标记的货物。',
  'tours.pib-tab-barang.excel.title': '货物很多时',
  'tours.pib-tab-barang.excel.body': '几十上百行时，请导出 Excel、在其中编辑、再上传回来。比在屏幕上逐行修改快得多。',
  'tours.pib-tab-barang.next.title': '进入 Pungutan',
  'tours.pib-tab-barang.next.body': '点击「下一步」查看税费计算结果。',

  // ---- pib-tab-pungutan -------------------------------------------------
  'tours.pib-tab-pungutan.intro.title': 'Pungutan 标签页',
  'tours.pib-tab-pungutan.intro.body':
    '本单据的税费明细。数值依据「Transaksi」与「Barang」标签页的数据算出 — 若有异常，请回到那两处排查。',
  'tours.pib-tab-pungutan.jenis.title': '税费种类',
  'tours.pib-tab-pungutan.jenis.body':
    '<b>BM</b> 进口关税（由 HS 编码决定）、<b>PPN</b> 增值税、<b>PPh 22</b> 进口环节所得税、<b>PPnBM</b> 奢侈品销售税。并非全部都会出现 — 取决于货物种类。',
  'tours.pib-tab-pungutan.generate.title': '生成税费 (Generate Pungutan)',
  'tours.pib-tab-pungutan.generate.body':
    '打开右上角的 <b>Aksi</b>（操作）菜单，选择 <b>Generate Pungutan</b> 重新计算全部税费。若单据已有申报号，则取自 CEISA；若尚未有，则依据货物数据本地计算。每次修改价值或 HS 编码后都应重新运行。',
  'tours.pib-tab-pungutan.next.title': '进入 Pernyataan',
  'tours.pib-tab-pungutan.next.body': '点击「下一步」进入最后一个标签页。',

  // ---- pib-tab-pernyataan -----------------------------------------------
  'tours.pib-tab-pernyataan.intro.title': 'Pernyataan 标签页',
  'tours.pib-tab-pernyataan.intro.body': '最后一个标签页。您在此声明所填报的数据无误，因此填写之前请再核对一遍。',
  'tours.pib-tab-pernyataan.tanggungJawab.title': '责任人',
  'tours.pib-tab-pernyataan.tanggungJawab.body': '对本申报负责的人员姓名与职务。若不确定该填谁，请先向主管确认。',
  'tours.pib-tab-pernyataan.cetak.title': '先打印，再提交',
  'tours.pib-tab-pernyataan.cetak.body':
    '提交之前，请打开操作菜单选择 <b>Cetak Formulir Draft CEISA</b>（打印 CEISA 草稿表）。通读打印件是发现屏幕上漏看错误最可靠的办法。',
  'tours.pib-tab-pernyataan.kirim.title': '提交至 CEISA',
  'tours.pib-tab-pernyataan.kirim.body':
    '确认无误后，在同一菜单中选择 <b>Kirim</b>（提交）。提交后单据会获得注册号，更正只能通过修订进行。<br /><br />恭喜 — 您已完整走完一份 PIB 的全流程。',
};

// English tour copy (next-jarvis#854). UI names the tour points at use the
// app's English labels; CEISA option values stay as the dropdown shows them,
// with an English gloss.
const en: CopyDict = {
  'controls.next': 'Next',
  'controls.back': 'Back',
  'controls.done': 'Done',
  'controls.progress': 'Step {{current}} of {{total}}',
  'controls.clickToContinue': 'Click the highlighted element to continue.',
  'controls.interactHint': "Go ahead and fill it in — the guide continues on its own once you're done.",
  'controls.exploreHint': 'This menu is still clickable — feel free to look around; the guide will wait here.',
  'pill.label': 'Guide',
  'pill.aria': 'Open the guide for this page',
  'pill.pageGuide': 'Guide for this page',
  'pill.tabGuide': 'Guide for the {{tab}} tab',
  'pill.menuGuide': 'Jarvis menu guide',
  'pill.section.split': 'Document splitting guide only',
  'pill.onboarding': 'Getting started',
  'tours.dashboard-welcome.intro.title': 'Welcome to Jarvis',
  'tours.dashboard-welcome.intro.body':
    'Jarvis helps you prepare customs documents and submit them to CEISA — the Indonesian Customs system. This short guide shows you where to start. You can exit any time with the ✕ button or Esc.',
  'tours.dashboard-welcome.stats.title': 'Activity summary',
  'tours.dashboard-welcome.stats.body':
    "Here you can see how many documents you've created and their status. The first time you log in, this section is empty — that's normal.",
  'tours.dashboard-welcome.nav.title': 'The menu on the left',
  'tours.dashboard-welcome.nav.body':
    'All your work starts here. The menu is grouped by type of work, not by document — so import and export activities sit in the same group.',
  'tours.dashboard-welcome.search.title': 'Search the menu',
  'tours.dashboard-welcome.search.body':
    "If you forget where a menu is, just type its name here. It's faster than opening the groups one by one.",
  'tours.dashboard-welcome.kepabeanan.title': 'Start with Customs',
  'tours.dashboard-welcome.kepabeanan.body':
    "This is the group you'll use most. Inside it are <b>Create Declaration</b> (create a new document), <b>Draft</b> (documents not yet submitted), and <b>Customs Documents</b> (documents already submitted).<br /><br />Click it to open.",
  'tours.dashboard-welcome.help.title': 'The guide is always here',
  'tours.dashboard-welcome.help.body':
    "Before you continue: click your account at the bottom of the menu to open the account menu. <b>Guide</b> is in there — click it any time to replay the guide for the page you're on, or to open the full walkthrough of every Jarvis menu.<br /><br />So there's nothing to memorize — you can always come back here.",
  'tours.dashboard-welcome.buatAju.title': 'Start here: Create Declaration',
  'tours.dashboard-welcome.buatAju.body':
    'This is where you create a new document. To continue an existing draft, use <b>Draft</b> right below it.<br /><br />Click <b>Create Declaration</b> to create your first PIB — the next guide will appear there.',
  'tours.sidebar-map.intro.title': 'Jarvis menu map',
  'tours.sidebar-map.intro.body':
    'This guide briefly explains each menu group so you know where to go. No need to memorize it — you can open this guide any time.',
  'tours.sidebar-map.mgmtDashboard.title': 'Management Dashboard',
  'tours.sidebar-map.mgmtDashboard.body':
    'Management-level reports: document volume, costs, and trends per period. Usually used by managers, not day-to-day staff.',
  'tours.sidebar-map.manifest.title': 'Manifest',
  'tours.sidebar-map.manifest.body':
    "For vessel manifest documents (BC 1.1) — the list of all cargo carried by one means of transport. Unlike a PIB, which only covers one importer's goods.",
  'tours.sidebar-map.kepabeanan.title': 'Customs',
  'tours.sidebar-map.kepabeanan.body':
    'The main group. <b>Create Declaration</b> creates a new document, <b>Draft</b> holds those not yet submitted, <b>Customs Documents</b> holds those already submitted, <b>Responses</b> shows replies from Customs, and <b>Block Monitoring</b> tracks documents that are on hold.',
  'tours.sidebar-map.kuota.title': 'Import / Export Quota',
  'tours.sidebar-map.kuota.body':
    'Track shipment quotas and reference prices. Useful if your company has an import volume limit to stay within.',
  'tours.sidebar-map.cukai.title': 'Excise',
  'tours.sidebar-map.cukai.body':
    'For excisable goods (CK) only — delivery orders, CK documents, bill of materials, and inventory. Relevant only if your company holds an excise license.',
  'tours.sidebar-map.customerApproval.title': 'Customer Approval',
  'tours.sidebar-map.customerApproval.body':
    "If your documents need customer approval before they're submitted to Customs, the flow is set up here — including who the reviewers are and which email group requests go to.",
  'tours.sidebar-map.creditTopup.title': 'Credit Top-up',
  'tours.sidebar-map.creditTopup.body':
    'Submitting documents uses credits. Here you top up your balance and see your usage history.',
  'tours.sidebar-map.langganan.title': 'Subscription',
  'tours.sidebar-map.langganan.body': "Your company's Jarvis subscription plan and its validity period.",
  'tours.sidebar-map.berita.title': 'News',
  'tours.sidebar-map.berita.body':
    "A digest of economic and trade news from several sources, so you don't have to leave the app.",
  'tours.sidebar-map.layananLainnya.title': 'Other Services',
  'tours.sidebar-map.layananLainnya.body':
    'Handy tools: file compression, PDF splitting, and the like. Often used to tidy up documents before uploading.',
  'tours.sidebar-map.master.title': 'Master Data',
  'tours.sidebar-map.master.body':
    'Master data: users, CEISA accounts, email, and other settings. Usually only your company admin can change it.',
  'tours.sidebar-map.preferences.title': 'Language and appearance',
  'tours.sidebar-map.preferences.body':
    'Click your account at the bottom of the menu to change the language. The theme is set in <b>Settings</b>. Your choices are saved for your next visit.',
  'tours.buat-aju.intro.title': 'Choose a document type',
  'tours.buat-aju.intro.body':
    'Every customs document has its own BC code. What you choose here determines the form and validation rules used from then on, so choose correctly from the start.',
  'tours.buat-aju.export.title': 'Export — BC 3.0',
  'tours.buat-aju.export.body':
    'PEB (Export Goods Declaration), for goods leaving Indonesia. The flow is similar to a PIB, but the data is different.',
  'tours.buat-aju.ppftz.title': 'PPFTZ',
  'tours.buat-aju.ppftz.body':
    'For goods moving to and from free trade zones such as Batam. Choose this only if your location really is in a free trade zone.',
  'tours.buat-aju.import.title': 'Import — BC 2.0 (PIB)',
  'tours.buat-aju.import.body':
    "This is what we'll learn: the <b>PIB</b>, the Import Goods Declaration. It's the document you submit to Customs for goods entering Indonesia.<br /><br />Click this card to start.",
  'tours.doc-pabean-upload.intro.title': 'Start from the original documents',
  'tours.doc-pabean-upload.intro.body':
    "Jarvis doesn't ask you to retype everything. Upload the documents from your supplier — invoice, packing list, B/L — and Jarvis reads them and fills in the PIB form for you.",
  'tours.doc-pabean-upload.folderDropAll.title': 'Drag them all in at once',
  'tours.doc-pabean-upload.folderDropAll.body':
    "Got a stack of files? Drop them all here — or anywhere on this page. JARVIS reads each file and puts it in the matching folder. Then <b>you check</b> each document in the preview on the right: click <b>Next</b> until you've seen every page, then tick <b>Checked</b>.",
  'tours.doc-pabean-upload.folderInvoice.title': 'The folder sets the document type',
  'tours.doc-pabean-upload.folderInvoice.body':
    'Files you drop into the <b>Invoice</b> folder are read as an Invoice — every page, including pages with charges such as "Ocean Freight". JARVIS only <b>warns you</b> if the content looks different; it never changes the type silently.<br /><br />Wrong folder? Drag the file to another folder, or change it with the document type button on its row.',
  'tours.doc-pabean-upload.folderChecklist.title': 'Completeness',
  'tours.doc-pabean-upload.folderChecklist.body':
    "A PIB needs a <b>BL / AWB</b> and an <b>Invoice</b>. This list shows what's still missing and which warnings haven't been answered. The button becomes active once everything is in order — next, you'll check the type and pages of each document in Review.",
  'tours.doc-pabean-upload.dropzone.gate': 'Upload at least one file to continue.',
  'tours.doc-pabean-upload.dropzone.title': 'Upload files',
  'tours.doc-pabean-upload.dropzone.body':
    'Drag PDF files into this area, or click to choose. You can upload several documents at once.',
  'tours.doc-pabean-upload.docType.gate': 'Some files still have no document type. Set a type for all of them first.',
  'tours.doc-pabean-upload.docType.title': 'Set the type of each document',
  'tours.doc-pabean-upload.docType.body':
    'Every file needs a type: Invoice, Packing List, Bill of Lading, COO, and so on. Jarvis guesses automatically, but <b>check its guess</b> — the wrong type sends data to the wrong fields.',
  'tours.doc-pabean-upload.autoSplit.title': "Auto split — don't skip this",
  'tours.doc-pabean-upload.autoSplit.body':
    "One PDF file often contains <b>several documents at once</b> — for example, an invoice and a packing list scanned together. This button reads the PDF and splits it into separate documents.<br /><br />Getting the split right matters: Jarvis reads each document according to its type, so make sure the number of documents here matches what's in the original file.<br /><br />The next steps explain how to split it yourself if the automatic result isn't quite right.",
  'tours.doc-pabean-upload.preview.title': 'Open a file to preview it',
  'tours.doc-pabean-upload.preview.body':
    "<b>Click one of the PDF files in this list</b> to open it in the panel on the right.<br /><br />Once it's open, the guide moves on to how to split one file into several documents.",
  'tours.doc-pabean-upload.reset.title': 'Uploaded the wrong file?',
  'tours.doc-pabean-upload.reset.body': 'This button clears the list so you can start over.',
  'tours.doc-pabean-upload.process.title': 'Process the documents',
  'tours.doc-pabean-upload.process.body':
    'This button changes with each stage: <b>Process</b> to read the documents, then <b>Upload</b>, then <b>Continue to review</b>. Reading can take a minute or two for long documents.<br /><br />Click to continue.',
  'tours.doc-pabean-upload.splitIntro.title': 'Splitting one file into several documents',
  'tours.doc-pabean-upload.splitIntro.body':
    "This panel sets <b>how many documents</b> Jarvis reads from one PDF file. One segment = one document.<br /><br />If the <b>Auto split</b> result is already correct, you don't need to change anything. This guide is for when the result isn't quite right, or when you want to split the file yourself.",
  'tours.doc-pabean-upload.segmentAdd.gate': 'Click Add segment once — the guide continues on its own after that.',
  'tours.doc-pabean-upload.segmentAdd.title': 'Step 1 — create a segment',
  'tours.doc-pabean-upload.segmentAdd.body':
    'Click <b>Add segment</b> <b>once</b> to create the first segment. The guide moves on as soon as the segment appears — no need to add more segments yet.<br /><br />The newly created segment becomes the <b>active</b> segment: the pages you pick next go into it. Once this segment is filled, you can add another segment for the next document in the same file.',
  'tours.doc-pabean-upload.segmentPages.gate':
    "There's still an empty segment. Pick pages for it, or delete it with the trash icon on its right.",
  'tours.doc-pabean-upload.segmentPages.title': 'Step 2 — pick its pages',
  'tours.doc-pabean-upload.segmentPages.body':
    "Click a page thumbnail to add it to the active segment. Click it again to remove it. Each page can belong to only one segment.<br /><br />If this file contains more than one document, repeat the pattern: <b>Add segment</b> again, then pick pages for that segment — for example, pages 1–2 for the invoice and page 3 for the packing list.<br /><br />A segment that's created but left empty will hold up the process; just delete it if you don't need it.",
  'tours.doc-pabean-upload.segmentRow.title': 'Step 3 — check each segment',
  'tours.doc-pabean-upload.segmentRow.body':
    'Each row shows the pages already in that segment. Click a row to make it the active segment before picking more pages.<br /><br />The message <i>"Each segment must have at least 1 page"</i> means there\'s still an empty segment — fill it or delete it first, because the extract button can\'t be used while that message is shown.',
  'tours.doc-pabean-upload.segmentType.title': 'Step 4 — set the document type',
  'tours.doc-pabean-upload.segmentType.body':
    'Choose a <b>Document type</b> for each segment: Invoice, Packing List, Bill of Lading, COO, and so on.<br /><br />This determines which PIB fields its content is read into. A segment without the right type will be read into the wrong place.',
  'tours.doc-pabean-upload.segmentOriginal.title': 'Step 5 — what happens to the original file?',
  'tours.doc-pabean-upload.segmentOriginal.body':
    'If <b>Remove original file</b> is ticked, the combined file is replaced by its split parts. Leave it ticked in normal cases — otherwise the combined file is processed too and your documents are counted twice.',
  'tours.doc-pabean-upload.segmentExtract.title': 'Step 6 — extract',
  'tours.doc-pabean-upload.segmentExtract.body':
    'This button splits the file according to the segments you set up. After that, each segment appears as its own file in the list on the left, and you just need to make sure its document type is correct.',
  'tours.doc-pabean-upload-export.intro.title': 'Upload export documents',
  'tours.doc-pabean-upload-export.intro.body':
    'The flow is the same as for imports: upload the documents you have, Jarvis reads them, and the results go into the <b>PEB</b> form (BC 3.0).<br /><br />The difference is in the document types and the form fields later — this is a declaration for goods <b>leaving</b> Indonesia.',
  'tours.doc-pabean-upload-export.dropzone.title': 'Upload files',
  'tours.doc-pabean-upload-export.dropzone.body':
    'Drag PDF files into this area or click to choose. For exports this is usually the invoice, packing list, and transport documents.',
  'tours.doc-pabean-upload-export.dropzone.gate': 'Upload at least one file to continue.',
  'tours.doc-pabean-upload-export.docType.title': 'Set the type of each document',
  'tours.doc-pabean-upload-export.docType.body':
    'Every file needs a type. Jarvis guesses automatically — check its guess, because the wrong type sends the content to the wrong fields. Shipping instructions from the forwarder get the type 000 - Shipping Instruction, not AWB.',
  'tours.doc-pabean-upload-export.docType.gate':
    'Some files still have no document type. Set a type for all of them first.',
  'tours.doc-pabean-upload-export.autoSplit.title': 'Auto split',
  'tours.doc-pabean-upload-export.autoSplit.body':
    "If one PDF contains several documents, this button reads it and splits it into separate documents. Make sure the number of documents after splitting matches what's in the original file.",
  'tours.doc-pabean-upload-export.reset.title': 'Uploaded the wrong file?',
  'tours.doc-pabean-upload-export.reset.body': 'This button clears the list so you can start over.',
  'tours.doc-pabean-upload-export.process.title': 'Process the documents',
  'tours.doc-pabean-upload-export.process.body':
    'This button changes with each stage: <b>Process</b>, then <b>Upload</b>, then on to the PEB form.<br /><br />Click to continue.',
  'tours.export-form.intro.title': 'PEB form (BC 3.0)',
  'tours.export-form.intro.body':
    "This is the <b>Export Goods Declaration</b> form — the document for goods leaving Indonesia. Jarvis has already filled in part of it from the documents you uploaded; check the results, fix anything that's wrong, and complete what's empty.<br /><br />This guide introduces each tab and the submit button.",
  'tours.export-form.actions.title': 'Actions menu',
  'tours.export-form.actions.body':
    'Other actions are available here, such as printing the form and revisions. Reading the printout before you submit is a good way to catch mistakes you missed on screen.',
  'tours.export-form.submit.title': 'Submit to CEISA',
  'tours.export-form.submit.body':
    'Submits the PEB to CEISA. Make sure every tab is free of errors before you press it — once submitted, corrections are made through a revision from the Actions menu.',
  'tours.export-form.tabs.title': 'Eight tabs',
  'tours.export-form.tabs.body':
    "PEB data is split across eight tabs. A tab marked in <b>red</b> means something is still wrong or missing.<br /><br />There's no separate Pungutan tab as in the PIB — for exports, <b>Export Duty</b>, <b>PPh 22</b>, and <b>Palm Oil Levy</b> are inside the Transaction tab.<br /><br />Each tab has its own guide: open a tab and its guide appears the first time you open it.",
  'tours.peb-tab-header.intro.title': 'Header tab',
  'tours.peb-tab-header.intro.body':
    'The basic identity of the export document: where the goods are loaded, which customs office handles it, and what kind of export this is.',
  'tours.peb-tab-header.pelabuhan.title': 'Export Loading Port',
  'tours.peb-tab-header.pelabuhan.body':
    'The port where the goods are loaded for export. Match it against your transport documents.',
  'tours.peb-tab-header.kantor.title': 'Origin Loading Customs Office',
  'tours.peb-tab-header.kantor.body':
    "The customs office where the goods are first loaded. It's partly filled in automatically from the port you chose — check the result.",
  'tours.peb-tab-header.kantorEkspor.title': 'Export Loading Customs Office',
  'tours.peb-tab-header.kantorEkspor.body':
    "The customs office where the goods are actually exported. Same as the origin loading office if the goods aren't moved first.",
  'tours.peb-tab-header.jenisEkspor.title': 'Export Type',
  'tours.peb-tab-header.jenisEkspor.body':
    'The export scheme for this shipment. The default is <b>1 - EKSPOR BIASA</b> (regular export); other options are for specific schemes.',
  'tours.peb-tab-header.kategori.title': 'Export Category',
  'tours.peb-tab-header.kategori.body':
    "The shipment category, for example <b>10 - UMUM</b> (general). Choose according to your company's scheme; if in doubt, ask your customs team.",
  'tours.peb-tab-header.caraDagang.title': 'Trade Method',
  'tours.peb-tab-header.caraDagang.body':
    'The trade terms agreed with the buyer, for example <b>1 - BIASA</b> (regular). Match it against your contract or invoice.',
  'tours.peb-tab-header.caraBayar.title': 'Payment Method',
  'tours.peb-tab-header.caraBayar.body':
    'The payment method for this transaction, for example <b>1 - BIASA/TUNAI</b> (regular/cash). The <b>Payment Notes</b> field next to it is for additional notes.',
  'tours.peb-tab-header.komoditi.title': 'Commodity',
  'tours.peb-tab-header.komoditi.body':
    'The options are <b>1 - MIGAS</b> or <b>2 - NON MIGAS</b> — whether the goods are oil and gas.',
  'tours.peb-tab-header.curah.title': 'Bulk',
  'tours.peb-tab-header.curah.body': 'Marks whether the goods are shipped in bulk (without unit packaging).',
  'tours.peb-tab-header.barangKiriman.title': 'Consignment Goods Export',
  'tours.peb-tab-header.barangKiriman.body':
    'Marks shipments sent through a postal or courier operator. The default is <b>T - BUKAN EKSPOR BARANG KIRIMAN</b> (not a consignment goods export).',
  'tours.peb-tab-header.next.title': 'Continue to Entity',
  'tours.peb-tab-header.next.body':
    'Click <b>Next</b>. The guide for the next tab appears the first time you open that tab.',
  'tours.peb-tab-entitas.intro.title': 'Entity tab',
  'tours.peb-tab-entitas.intro.body':
    'The parties in this export, starting with the <b>Exporter NPWP</b>. Note that the consignee and the buyer are kept separate.',
  'tours.peb-tab-entitas.eksportir.title': 'Exporter Name',
  'tours.peb-tab-entitas.eksportir.body':
    "The name of the exporting company. Match it against the company's official documents.",
  'tours.peb-tab-entitas.alamatEksportir.title': 'Exporter Address',
  'tours.peb-tab-entitas.alamatEksportir.body': "The exporter's registered address, as on its official documents.",
  'tours.peb-tab-entitas.penerima.title': 'Consignee Name',
  'tours.peb-tab-entitas.penerima.body':
    'The party that <b>receives</b> the goods abroad, as on the transport documents.',
  'tours.peb-tab-entitas.negaraPenerima.title': 'Consignee Country',
  'tours.peb-tab-entitas.negaraPenerima.body':
    "The country where the consignee is located. It can differ from the buyer's country.",
  'tours.peb-tab-entitas.pembeli.title': 'Buyer Name',
  'tours.peb-tab-entitas.pembeli.body':
    'The party that <b>buys</b> the goods — often different from the consignee. Fill it in as on the invoice.',
  'tours.peb-tab-entitas.negaraPembeli.title': 'Buyer Country',
  'tours.peb-tab-entitas.negaraPembeli.body':
    "The buyer's country. If it differs from the consignee's country, both are still reported separately.",
  'tours.peb-tab-entitas.ppjk.title': 'PPJK',
  'tours.peb-tab-entitas.ppjk.body':
    'Fill in this section if the clearance is handled by a customs broker. Nearby are also the <b>PPJK ID Number</b> and <b>PPJK Address</b>.',
  'tours.peb-tab-entitas.konsolidasi.title': 'Consolidation Location',
  'tours.peb-tab-entitas.konsolidasi.body':
    'Fill this in if the goods are combined with other shipments before loading.',
  'tours.peb-tab-entitas.next.title': 'Continue to Document',
  'tours.peb-tab-entitas.next.body': 'Click <b>Next</b> to move to the next tab.',
  'tours.peb-tab-document.intro.title': 'Document tab',
  'tours.peb-tab-document.intro.body': 'Supporting export documents with their numbers and dates.',
  'tours.peb-tab-document.daftar.title': 'Document list',
  'tours.peb-tab-document.daftar.body':
    'Add, edit, copy, or delete rows here. Check the numbers and dates against the original files. A document can also be linked to specific goods through <b>Attach Document to Goods</b>.',
  'tours.peb-tab-document.next.title': 'Continue to Transport',
  'tours.peb-tab-document.next.body': 'Click <b>Next</b> to move to the next tab.',
  'tours.peb-tab-pengangkut.intro.title': 'Origin loading port',
  'tours.peb-tab-pengangkut.intro.body':
    "The Transport tab describes the goods' outbound journey. It starts with the port where the goods are first loaded.",
  'tours.peb-tab-pengangkut.muatEkspor.title': 'Export loading port',
  'tours.peb-tab-pengangkut.muatEkspor.body':
    "The port where the goods are actually exported. Same as the origin loading port if the goods aren't moved first.",
  'tours.peb-tab-pengangkut.penimbunan.title': 'Storage location',
  'tours.peb-tab-pengangkut.penimbunan.body': 'Where the goods are stored before loading.',
  'tours.peb-tab-pengangkut.tanggal.title': 'Estimated export date',
  'tours.peb-tab-pengangkut.tanggal.body':
    'The estimated date the goods are exported, according to your transport schedule.',
  'tours.peb-tab-pengangkut.sarana.title': 'Transport Mode',
  'tours.peb-tab-pengangkut.sarana.body':
    'The mode of transport carrying the goods out. Copy it from the transport documents.',
  'tours.peb-tab-pengangkut.tujuan.title': 'Destination port',
  'tours.peb-tab-pengangkut.tujuan.body': "The goods' final destination port.",
  'tours.peb-tab-pengangkut.negara.title': 'Export destination country',
  'tours.peb-tab-pengangkut.negara.body':
    "The goods' destination country. Match it against the invoice and transport documents.",
  'tours.peb-tab-pengangkut.pemeriksaan.title': 'Inspection',
  'tours.peb-tab-pengangkut.pemeriksaan.body':
    '<b>Inspection Location</b>, with the <b>Inspection Customs Office</b> and <b>Inspection date</b> next to it — used when the goods are scheduled for inspection before loading.',
  'tours.peb-tab-pengangkut.next.title': 'On to Packaging',
  'tours.peb-tab-pengangkut.next.body': 'Click <b>Next</b> to move to the next tab.',
  'tours.peb-tab-kemasan.intro.title': 'Packaging & Containers tab',
  'tours.peb-tab-kemasan.intro.body': 'How many packages are being shipped, and which container the goods are in.',
  'tours.peb-tab-kemasan.kemasan.title': 'Package list',
  'tours.peb-tab-kemasan.kemasan.body':
    'Add and edit rows here. Match the quantities with the Packing List, and with the weights you entered on the Transaction tab.',
  'tours.peb-tab-kemasan.next.title': 'On to Transaction',
  'tours.peb-tab-kemasan.next.body': 'Click <b>Next</b> to move to the next tab.',
  'tours.peb-tab-transaksi.intro.title': 'Currency',
  'tours.peb-tab-transaksi.intro.body':
    'The Transaction tab is the financial side of this export — and where export levies are calculated. Start with the transaction currency.',
  'tours.peb-tab-transaksi.ndpbm.title': 'NDPBM',
  'tours.peb-tab-transaksi.ndpbm.body':
    'The base rate used to convert the currency above. If you are not sure which rate applies, ask your customs team.',
  'tours.peb-tab-transaksi.penyerahan.title': 'Delivery terms',
  'tours.peb-tab-transaksi.penyerahan.body':
    'The delivery terms agreed with the buyer. Match them with your contract or invoice.',
  'tours.peb-tab-transaksi.nilai.title': 'Export Value (Incoterm FOB)',
  'tours.peb-tab-transaksi.nilai.body':
    'The value of the exported goods, entered on an <b>FOB</b> basis as the field label says.',
  'tours.peb-tab-transaksi.asuransi.title': 'Insurance',
  'tours.peb-tab-transaksi.asuransi.body': 'The insurance value for this shipment, along with the insurance type.',
  'tours.peb-tab-transaksi.maklon.title': 'Toll Manufacturing Value',
  'tours.peb-tab-transaksi.maklon.body':
    'Filled in for toll manufacturing — goods owned by another party that you process and then re-export.',
  'tours.peb-tab-transaksi.beratKotor.title': 'Gross Weight (KGM)',
  'tours.peb-tab-transaksi.beratKotor.body':
    'Weight including packaging, in kilograms. Match it with the Packing List.',
  'tours.peb-tab-transaksi.beratBersih.title': 'Net Weight (KGM)',
  'tours.peb-tab-transaksi.beratBersih.body':
    'Weight of the goods alone, in kilograms. This tab also has the <b>Export Duty Info</b>, <b>PPh 22 Info</b>, and <b>Palm Oil Levy Info</b> panels, plus <b>Export Duty Preview</b> — run them again whenever the value or the goods change.',
  'tours.peb-tab-transaksi.next.title': 'On to Goods',
  'tours.peb-tab-transaksi.next.body': 'Click <b>Next</b> to move to the next tab.',
  'tours.peb-tab-barang.intro.title': 'Goods tab',
  'tours.peb-tab-barang.intro.body': 'Each type of exported goods is listed as one row.',
  'tours.peb-tab-barang.baris.title': 'Goods list',
  'tours.peb-tab-barang.baris.body':
    'The number of rows must match the invoice and Packing List. Each row holds the HS Code, quantity, unit, and weight; the search box looks up by description or HS Code.',
  'tours.peb-tab-barang.next.title': 'On to Statement',
  'tours.peb-tab-barang.next.body': 'Click <b>Next</b> to reach the last tab.',
  'tours.peb-tab-pernyataan.intro.title': 'Name',
  'tours.peb-tab-pernyataan.intro.body':
    'The last tab. The name of the person responsible for this export declaration.',
  'tours.peb-tab-pernyataan.jabatan.title': 'Position',
  'tours.peb-tab-pernyataan.jabatan.body': "That person's position in the company.",
  'tours.peb-tab-pernyataan.tempat.title': 'Place',
  'tours.peb-tab-pernyataan.tempat.body': 'The place where this declaration is made.',
  'tours.peb-tab-pernyataan.tanggal.title': 'Date',
  'tours.peb-tab-pernyataan.tanggal.body': 'The date the declaration is made.',
  'tours.peb-tab-pernyataan.submit.title': 'Submit to CEISA',
  'tours.peb-tab-pernyataan.submit.body':
    'Sends the PEB to CEISA. Make sure every tab is free of errors before you press it — once sent, corrections are made through a revision from the Actions menu.',
  'tours.doc-pabean-draft-list.intro.title': 'Draft list',
  'tours.doc-pabean-draft-list.intro.body':
    'Drafts are documents that have <b>not been sent</b> to Customs yet — anything can still be changed. Tick several rows to delete them at once.',
  'tours.doc-pabean-draft-list.search.title': 'Search drafts',
  'tours.doc-pabean-draft-list.search.body':
    'Search by AJU number, importer name, or invoice number. Very handy once your drafts start piling up.',
  'tours.doc-pabean-draft-list.open.title': 'Open a draft',
  'tours.doc-pabean-draft-list.open.body':
    'Click any row to open its PIB form. That is where you check what Jarvis read and fill in anything missing.<br /><br />Click a draft to continue.',
  'tours.manifest-from-pabean.note.title': 'Only PEBs from JARVIS',
  'tours.manifest-from-pabean.note.body':
    'This list only contains PEBs created through JARVIS that have received an <b>NPE</b> response. PEBs created outside JARVIS can be added later from inside the draft via <b>Upload PEB Susulan</b>.',
  'tours.manifest-from-pabean.table.title': 'Tick the PEBs loaded together',
  'tours.manifest-from-pabean.table.body':
    'Each PEB becomes one house BL/AWB. <b>The order you tick them in</b> becomes the pos order in the manifest, and the manifest header follows the first PEB. PEBs labelled "Already in a draft" can still be selected.',
  'tours.manifest-from-pabean.create.title': 'Create Manifest',
  'tours.manifest-from-pabean.create.body':
    'The draft is built from the PEB data — no upload and no AI extraction — then opened in the editor. The Master BL can be filled in later.',
  'tours.manifest-from-pabean-inward.note.title': 'Only PIBs from JARVIS',
  'tours.manifest-from-pabean-inward.note.body':
    'This list only contains PIBs created through JARVIS that have received an <b>SPPB</b> response. House BLs/AWBs from PIBs outside JARVIS can be added later from inside the draft via <b>Upload BL/AWB Susulan</b>.',
  'tours.manifest-from-pabean-inward.table.title': 'Tick the PIBs consolidated together',
  'tours.manifest-from-pabean-inward.table.body':
    "Each PIB becomes one house BL/AWB. <b>The order you tick them in</b> becomes the pos order in the manifest, and the manifest header follows the first PIB. The BC 1.1 column shows the carrier's original manifest that the PIB refers to.",
  'tours.manifest-from-pabean-inward.create.title': 'Create Manifest',
  'tours.manifest-from-pabean-inward.create.body':
    'The inward draft is built from the PIB data — no upload and no AI extraction — then opened in the editor. The Master BL/AWB can be filled in later.',
  'tours.doc-pabean-list.intro.title': 'Submitted documents',
  'tours.doc-pabean-list.intro.body':
    'Unlike Drafts, this page holds documents that have <b>already</b> been sent to CEISA. Here you track their progress.',
  'tours.doc-pabean-list.credits.title': 'Credit balance',
  'tours.doc-pabean-list.credits.body':
    'Submitting documents uses credits. Keep an eye on your balance here, and top up via the Credit Top-up menu before it runs low.',
  'tours.doc-pabean-list.filters.title': 'Filter and sort',
  'tours.doc-pabean-list.filters.body':
    'Filter by status, date, or branch. If your company has several branches, the branch filter limits the view to your own.',
  'tours.doc-pabean-list.status.title': 'Read the status column',
  'tours.doc-pabean-list.status.body':
    "The status shows where the document stands with Customs. Watch especially for documents that are <b>rejected</b> or <b>blocked</b> — both need follow-up, and the document's Actions menu offers a <b>Revision</b> option to resubmit the correction.",
  'tours.doc-pabean-list.report.title': 'Download report',
  'tours.doc-pabean-list.report.body':
    'Generates a summary of documents within a given date range, for internal reporting or audits.',
  'tours.pib-edit-overview.intro.title': 'PIB form',
  'tours.pib-edit-overview.intro.body':
    'This is the heart of your work. Jarvis has already filled in some fields from the documents you uploaded — <b>check the results, fix anything wrong, and complete what is still empty</b>.<br /><br />The original document appears on the left so you can compare side by side.',
  'tours.pib-edit-overview.errors.title': 'Error list',
  'tours.pib-edit-overview.errors.body':
    'This red panel lists everything still blocking submission. Click an error to jump straight to its field.<br /><br />There is also <b>Bot Assist</b>, which walks you through the fixes one by one — very helpful when there are many errors.',
  'tours.pib-edit-overview.toolbar.title': 'Tools',
  'tours.pib-edit-overview.toolbar.body':
    'Download the data to Excel, edit it there, then upload it back — the fastest way when you have dozens or hundreds of goods rows. You can also sync with historical data to reuse entries from similar past documents.',
  'tours.pib-edit-overview.actions.title': 'Validate, print, submit',
  'tours.pib-edit-overview.actions.body':
    'The right order: <b>Validate</b> to make sure CEISA will accept the data, then <b>Print CEISA Draft Form</b> for a human check, and only then <b>Submit</b>.<br /><br />Once submitted, corrections can only be made through a revision — so do not skip the print step.',
  'tours.pib-edit-overview.footer.title': 'Moving between tabs',
  'tours.pib-edit-overview.footer.body':
    'The Previous and Next buttons step through the tabs in order. Switching tabs saves your changes automatically — there is no separate Save button.',
  'tours.pib-edit-overview.tabs.title': 'Nine tabs',
  'tours.pib-edit-overview.tabs.body':
    'The PIB data is split across nine tabs. A tab shown in <b>red</b> still has something wrong or missing inside.<br /><br />Each tab has its own guide: open a tab and its guide appears automatically the first time.',
  'tours.pib-tab-header.intro.title': 'Header tab',
  'tours.pib-tab-header.intro.body':
    "The document's basic identity: which port the goods enter through, which customs office handles them, and what they are for. What you enter here decides the rules the following tabs use.",
  'tours.pib-tab-header.pelabuhan.title': 'Port',
  'tours.pib-tab-header.pelabuhan.body':
    'The port where the goods are unloaded. Match it with the port stated on the Bill of Lading.',
  'tours.pib-tab-header.kantorPabean.title': 'Customs Office',
  'tours.pib-tab-header.kantorPabean.body':
    'The Customs office handling the entry of the goods. This field follows the port you choose automatically — but if you change the port, <b>check the office again</b>.',
  'tours.pib-tab-header.jenisPib.title': 'PIB Type',
  'tours.pib-tab-header.jenisPib.body':
    'The options are <b>1 - BIASA</b> (regular) or <b>2 - BERKALA</b> (periodic). Periodic is for the periodic declaration scheme; if you are not sure whether your company uses it, check with your supervisor or customs team first.',
  'tours.pib-tab-header.jenisImpor.title': 'Import Type',
  'tours.pib-tab-header.jenisImpor.body':
    'Why the goods are coming in: <b>1 - UNTUK DIPAKAI</b> (for use), <b>2 - SEMENTARA</b> (temporary), <b>5 - RUSH HANDLING</b>, or <b>9 - GABUNGAN 1 DAN 2</b> (combined 1 and 2).<br /><br />Choose the one that fits your import. If in doubt, compare with earlier import documents for similar goods.',
  'tours.pib-tab-header.caraBayar.title': 'Payment Method',
  'tours.pib-tab-header.caraBayar.body':
    'The payment method for this transaction. <b>1 - BIASA/TUNAI</b> (regular/cash) and <b>2 - BERKALA</b> (periodic) are the most common; the rest name the payment terms with the supplier — Open Account, T/T, Advance Payment, Sight L/C, collection, or no payment.<br /><br />Match it with the payment terms stated on your invoice or contract.',
  'tours.pib-tab-header.next.title': 'On to Entity',
  'tours.pib-tab-header.next.body':
    'Click Next. Your changes are saved automatically, and the guide for the next tab will appear.',
  'tours.pib-tab-entitas.intro.title': 'Entity tab',
  'tours.pib-tab-entitas.intro.body':
    'The parties in this import, each with its own section: Importer, Goods Owner, Shipper, Seller, PPJK, and Centralization. Each section is reported separately.',
  'tours.pib-tab-entitas.importir.title': 'Importer',
  'tours.pib-tab-entitas.importir.body':
    "The company registered as the importer, including its NPWP and address. Match it with that company's official documents.",
  'tours.pib-tab-entitas.pemilik.title': 'Goods Owner',
  'tours.pib-tab-entitas.pemilik.body':
    "The party that actually owns the goods. It can be the same as the importer or different — for example when the import is handled on someone else's behalf.",
  'tours.pib-tab-entitas.pengirim.title': 'Shipper',
  'tours.pib-tab-entitas.pengirim.body':
    'The party sending the goods from abroad, as stated on the transport documents.',
  'tours.pib-tab-entitas.penjual.title': 'Seller',
  'tours.pib-tab-entitas.penjual.body':
    'The party selling the goods. Often the same as the shipper, but not always — fill it in according to the invoice.',
  'tours.pib-tab-entitas.pemusatan.title': 'Centralization',
  'tours.pib-tab-entitas.pemusatan.body':
    'Filled in if your company uses the centralized declaration scheme. Otherwise, leave it empty.',
  'tours.pib-tab-entitas.ppjk.title': 'PPJK',
  'tours.pib-tab-entitas.ppjk.body':
    'Fill in this section if customs clearance is handled by a representative. If the company handles it itself, you can leave this section empty.',
  'tours.pib-tab-entitas.next.title': 'On to Document',
  'tours.pib-tab-entitas.next.body': 'Click Next to move to the Document tab.',
  'tours.pib-tab-dokumen.intro.title': 'Document tab',
  'tours.pib-tab-dokumen.intro.body':
    'The list of supporting documents with their numbers and dates. This tab also has an HS Code filter and bulk filling of permit item numbers.',
  'tours.pib-tab-dokumen.wajib.title': 'Document list',
  'tours.pib-tab-dokumen.wajib.body':
    "Check each row's number and date against the original file. Dates on original documents are sometimes written with a two-digit year — make sure the year saved here is correct.",
  'tours.pib-tab-dokumen.tambah.title': 'Adding a document',
  'tours.pib-tab-dokumen.tambah.body':
    'This button opens a form to add documents that were not read automatically.<br /><br />Click to open it — the guide will close so it does not get in the way.',
  'tours.pib-tab-pengangkut.intro.title': 'Transport tab',
  'tours.pib-tab-pengangkut.intro.body':
    'Details of the vessel or aircraft carrying your goods. Customs uses this section to match your PIB with the vessel manifest they have already received.',
  'tours.pib-tab-pengangkut.sarana.title': 'Carrier name and voyage',
  'tours.pib-tab-pengangkut.sarana.body':
    'The vessel name or flight number, plus the voyage number. Copy it exactly as written on the Bill of Lading, spelling included.',
  'tours.pib-tab-pengangkut.bc11.title': 'BC 1.1 Number',
  'tours.pib-tab-pengangkut.bc11.body':
    "BC 1.1 is the manifest the carrier reports to Customs, listing the vessel's entire cargo. This number links your goods to that manifest.",
  'tours.pib-tab-pengangkut.pos.title': 'Pos and Sub-pos',
  'tours.pib-tab-pengangkut.pos.body':
    "Your goods' sequence number within the BC 1.1 manifest. If you do not have it, ask the shipping line or forwarder handling this shipment.",
  'tours.pib-tab-pengangkut.tanggalTutup.title': 'PU Closing Date',
  'tours.pib-tab-pengangkut.tanggalTutup.body':
    'The closing date of the general declaration (PU) for this carrier, as per the BC 1.1 data.',
  'tours.pib-tab-pengangkut.subpos.title': 'Sub-pos Number',
  'tours.pib-tab-pengangkut.subpos.body':
    'A breakdown under the Pos Number. Fill it in if the manifest you received lists it.',
  'tours.pib-tab-pengangkut.subsubpos.title': 'Sub-sub-pos Number',
  'tours.pib-tab-pengangkut.subsubpos.body':
    'The deepest level of breakdown under the Sub-pos. Often empty — fill it in only if it is actually listed.',
  'tours.pib-tab-pengangkut.pelTujuan.title': 'Port of Destination',
  'tours.pib-tab-pengangkut.pelTujuan.body':
    "The destination port of the goods. This form also has the <b>Port of Loading</b> and the carrier's <b>Flag</b> — match all three with the transport documents.",
  'tours.pib-tab-pengangkut.next.title': 'On to Packaging',
  'tours.pib-tab-pengangkut.next.body': 'Click Next to move to the Packaging & Containers tab.',
  'tours.pib-tab-kemasan.intro.title': 'Packaging',
  'tours.pib-tab-kemasan.intro.body':
    'This tab has two lists: <b>Packaging</b> at the top and <b>Containers</b> below it.',
  'tours.pib-tab-kemasan.jumlah.title': 'Package list',
  'tours.pib-tab-kemasan.jumlah.body':
    'The number and type of packages, e.g. "120 CARTON". Match them with the Packing List and with the weights you entered on the Transaction tab.',
  'tours.pib-tab-kemasan.petiKemas.title': 'Containers',
  'tours.pib-tab-kemasan.petiKemas.body': 'Container numbers with their size and type, as per the Bill of Lading.',
  'tours.pib-tab-kemasan.next.title': 'On to Transaction',
  'tours.pib-tab-kemasan.next.body': 'Click Next to move to the Transaction tab.',
  'tours.pib-tab-transaksi.intro.title': 'Currency',
  'tours.pib-tab-transaksi.intro.body':
    'The Transaction tab is the financial side of this import. Start with the currency used on the invoice.',
  'tours.pib-tab-transaksi.ndpbm.title': 'NDPBM',
  'tours.pib-tab-transaksi.ndpbm.body':
    'The base rate used to convert the currency above. If you are not sure which rate applies, ask your customs team.',
  'tours.pib-tab-transaksi.jenisTransaksi.title': 'Transaction Type',
  'tours.pib-tab-transaksi.jenisTransaksi.body':
    'The nature of this purchase transaction. Match it with your contract or invoice.',
  'tours.pib-tab-transaksi.incoterm.title': 'Incoterm',
  'tours.pib-tab-transaksi.incoterm.body':
    'The delivery terms agreed with the supplier — FOB, CIF, CFR, and so on. This choice decides which costs are already included in the invoice price.',
  'tours.pib-tab-transaksi.hargaBarang.title': 'Goods Price',
  'tours.pib-tab-transaksi.hargaBarang.body':
    'The value of the goods as per the invoice, in the currency you chose above.',
  'tours.pib-tab-transaksi.asuransi.title': 'Insurance',
  'tours.pib-tab-transaksi.asuransi.body':
    'The insurance value for this shipment. Whether you need to fill it in depends on the Incoterm you chose — match it with your invoice and policy.',
  'tours.pib-tab-transaksi.nilaiPabean.title': 'Customs Value in Rupiah',
  'tours.pib-tab-transaksi.nilaiPabean.body':
    'The calculated customs value, in rupiah. This is the figure used on the Pungutan tab, so check it before moving on.',
  'tours.pib-tab-transaksi.berat.title': 'Gross and Net Weight (KGM)',
  'tours.pib-tab-transaksi.berat.body':
    'Both in kilograms. <b>Net weight cannot be zero and cannot exceed gross weight</b> — this form rejects both. Match them with the Packing List.',
  'tours.pib-tab-transaksi.komponenBiaya.title': 'Cost Components',
  'tours.pib-tab-transaksi.komponenBiaya.body':
    'A breakdown of the components that make up the customs value. This dialog <b>must be opened and saved at least once</b> before the document can be submitted.<br /><br />Click to open it — the guide will close.',
  'tours.pib-tab-barang.intro.title': 'Goods tab',
  'tours.pib-tab-barang.intro.body':
    'Each type of goods is listed as one row, and Customs calculates the levies per row.',
  'tours.pib-tab-barang.baris.title': 'Goods list',
  'tours.pib-tab-barang.baris.body':
    'The number of rows must match the Invoice and Packing List. Each row holds the HS Code, description, quantity, unit, and weight — the HS Code determines the tariff, so check that first. This tab also has <b>Lartas</b> and <b>Non-Lartas</b> filters to separate flagged goods.',
  'tours.pib-tab-barang.excel.title': 'If you have a lot of goods',
  'tours.pib-tab-barang.excel.body':
    'For dozens or hundreds of rows, download to Excel, edit there, then upload it back. Much faster than fixing them one by one on screen.',
  'tours.pib-tab-barang.next.title': 'On to Pungutan',
  'tours.pib-tab-barang.next.body': 'Click Next to see the duty and tax calculation.',
  'tours.pib-tab-pungutan.intro.title': 'Pungutan tab',
  'tours.pib-tab-pungutan.intro.body':
    'The breakdown of duties and taxes for this document. The figures are calculated from the data on the Transaction and Goods tabs — if something looks off, trace it back there.',
  'tours.pib-tab-pungutan.jenis.title': 'Levy types',
  'tours.pib-tab-pungutan.jenis.body':
    '<b>BM</b> import duty (from the HS Code), <b>PPN</b> value-added tax, <b>PPh 22</b> import income tax, and <b>PPnBM</b> for luxury goods. Not all of them always appear — it depends on the type of goods.',
  'tours.pib-tab-pungutan.generate.title': 'Generate Pungutan',
  'tours.pib-tab-pungutan.generate.body':
    'Open the <b>Actions</b> menu at the top right, then choose <b>Generate Pungutan</b> to recalculate all duties and taxes. If the document already has an AJU number, the calculation comes from CEISA; if not, it is calculated locally from the goods data. Run it again whenever you change a value or HS Code.',
  'tours.pib-tab-pungutan.next.title': 'On to Statement',
  'tours.pib-tab-pungutan.next.body': 'Click Next to reach the last tab.',
  'tours.pib-tab-pernyataan.intro.title': 'Statement tab',
  'tours.pib-tab-pernyataan.intro.body':
    'The last tab. Here you declare that the reported data is correct, so check it once more before filling this in.',
  'tours.pib-tab-pernyataan.tanggungJawab.title': 'Who is responsible',
  'tours.pib-tab-pernyataan.tanggungJawab.body':
    'The name and position of the person responsible for this declaration. If you are not sure who to list, check with your supervisor first.',
  'tours.pib-tab-pernyataan.cetak.title': 'Print first, then submit',
  'tours.pib-tab-pernyataan.cetak.body':
    'Before submitting, open the actions menu and choose <b>Print CEISA Draft Form</b>. Reading the printout is the most reliable way to catch mistakes missed on screen.',
  'tours.pib-tab-pernyataan.kirim.title': 'Submit to CEISA',
  'tours.pib-tab-pernyataan.kirim.body':
    'When everything is correct, choose <b>Submit</b> from the same menu. Once submitted, the document gets a registration number and corrections can only be made through a revision.<br /><br />Congratulations — you have walked through a PIB from start to finish.',
};

const ONBOARDING_COPY: Record<LanguageCode, CopyDict> = { id, zh, en };

/**
 * Resolves a copy key in the given language, falling back to Indonesian and
 * finally to the key itself — the same contract as `useLanguage().t`, so a
 * missing string degrades visibly rather than rendering blank.
 *
 * Callers must resolve at tour (re)start, never at module scope: the popover's
 * language switcher restarts the tour before React re-renders, so a resolver
 * captured from a hook would still be serving the previous language.
 */
export const resolveTourCopy = (language: LanguageCode, key: string, vars?: Record<string, string | number>) => {
  const raw = ONBOARDING_COPY[language]?.[key] ?? ONBOARDING_COPY[DEFAULT_LANGUAGE][key] ?? key;
  if (!vars) {
    return raw;
  }
  return Object.entries(vars).reduce((text, [name, value]) => text.split(`{{${name}}}`).join(String(value)), raw);
};

/** Test-only view of the raw dictionaries, so parity checks can't be masked by the fallback. */
export const __ONBOARDING_COPY = ONBOARDING_COPY;
